import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useLayout } from '../../../context/LayoutContext';
import {
  Box,
  Typography,
  Grid,
  Paper,
  IconButton,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Tooltip,
  Divider,
  ToggleButtonGroup,
  ToggleButton,
  FormControlLabel,
  Switch,
  Autocomplete,
  Breadcrumbs,
  Link,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton as MuiIconButton,
  Select as MuiSelect,
  MenuItem as MuiMenuItem,
  InputLabel as MuiInputLabel,
  FormControl as MuiFormControl,
  Tab,
  Tabs,
  Checkbox,
  Alert as MuiAlert,
  Paper as MuiPaper  

} from '@mui/material';
import {
  ArrowBackIos,
  ArrowForwardIos,
  Today,
  Edit,
  Delete,
  Email,
  Send,
  DesktopWindows,
  Language,
  FilterList,
  FilterAlt,
  CalendarMonth,
  NavigateNext,
  Print,
  Add,
  AccessTime,
  Schedule,
  Person,
  Group,
  WatchLater,
  LocalHospital,
  Info,
  ArrowBack,
  ArrowForward,
  CalendarToday,
  DragIndicator,
  NavigateBefore,
  NavigateNext as NavigateNextIcon
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  useGetWeeklyShiftsByStaffQuery,
  useGetAvailableShiftsQuery,
  useAssignStaffToShiftMutation,
  useReOpenShiftMutation,
  useUpdateShiftMutation
} from '../../../slices/shiftApiSlice';
import { useGetUsersQuery } from '../../../slices/usersApiSlice';
import {
  useGetWeeklyAvailabilitiesByStaffQuery,
  useCreateAvailabilityMutation,
  useUpdateAvailabilityMutation,
  useDeleteAvailabilityMutation
} from '../../../slices/availabilityApiSlice';
import { useSelector } from 'react-redux';
import { useGetHospitalLocationsQuery } from '../../../slices/hospitalLocationApiSlice';


import { CATEGORY_OF_STAFF, REASON_FOR_REQUEST, SPECIALS_REASON } from '../../../utils/enums';

import { getLocalDateString } from '../../../utils/dateUtils';

import { dublinLocalToUtcISO, dublinTimeStr, dublinDateStr , dublinDateTimeInputStr} from '../../../utils/dublinTime';


// Add this style object near the top of your component
const scrollableTableStyle = {
  maxHeight: '70vh',
  overflowY: 'auto',
  overflowX: 'auto',
  border: '1px solid #ddd',
  borderRadius: '4px',
  position: 'relative',
  WebkitOverflowScrolling: 'touch',
  width: '100%',
};

// Update the fixedHeaderStyle
const fixedHeaderStyle = {
  position: 'sticky',
  top: 0,
  zIndex: 10,
  backgroundColor: 'white',
  boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
  display: 'flex',
  width: '100%',
};

// Job titles that should only be visible to admins (e.g. "ECT Nurse", "Bank HCA")
const isAdminOnlyJobTitle = (jobtitle) => {
  if (!jobtitle) return false;
  const trimmed = jobtitle.trim().toLowerCase();
  return trimmed.startsWith('ect') || trimmed.startsWith('bank');
};

// Add print styles component
const PrintStyles = () => (
  <style>{`
    @media print {
      .no-print {
        display: none !important;
      }
      .print-page {
        page-break-inside: avoid !important;
        break-inside: avoid !important;
      }
      * {
        -webkit-print-color-adjust: exact !important;
        print-color-adjust: exact !important;
        color-adjust: exact !important;
      }
      @-moz-document url-prefix() {
        .shift-card, .availability-card, .status-chip, .header-cell {
          box-shadow: inset 0 0 0 1000px var(--print-color) !important;
        }
      }
      body {
        margin: 0 !important;
        padding: 0 !important;
        background: white !important;
      }
      .print-container {
        width: 100% !important;
        margin: 0 !important;
        padding: 0 !important;
      }
      .calendar-table {
        width: 100% !important;
        table-layout: fixed !important;
        border-collapse: collapse !important;
      }
      .shift-text, .availability-text {
        color: #000 !important;
        font-weight: bold !important;
      }
      .print-text {
        font-size: 11pt !important;
        line-height: 1.4 !important;
      }
      @page {
        margin: 0.5in !important;
      }
      .staff-row {
        page-break-inside: avoid !important;
        break-inside: avoid !important;
      }
      .print-header {
        text-align: center !important;
        margin-bottom: 20px !important;
        border-bottom: 2px solid #000 !important;
        padding-bottom: 10px !important;
      }
      .shift-assigned {
        background-color: #4caf50 !important;
      }
      .shift-weekend-assigned {
        background-color: #ff9800 !important;
      }
      .shift-agency {
        background-color: #9c27b0 !important;
      }
      .shift-cancelled {
        background-color: #f44336 !important;
      }
      .shift-available {
        background-color: #2196f3 !important;
      }
      .availability-pending {
        background-color: #e3f2fd !important;
      }
      .availability-approved {
        background-color: #e8f5e8 !important;
      }
      .availability-rejected {
        background-color: #ffebee !important;
      }
    }
  `}</style>
);

// Shift time presets (from StaffAvailability)
const SHIFT_PRESETS = {
  'LD': { start: '08:00', end: '20:00', label: 'Long Day (8:00-20:00)' },
  'ND': { start: '20:00', end: '08:00', label: 'Night Duty (20:00-8:00)' },
  'AM': { start: '08:00', end: '16:00', label: 'Morning (8:00-16:00)' },
  'PM': { start: '14:00', end: '20:00', label: 'Afternoon (14:00-20:00)' },
  'TW': { start: '20:00', end: '00:00', label: 'Twilight (20:00-00:00)' },
  'ELD': { start: '07:30', end: '20:30', label: 'Extended Long Day (7:30-20:30)' },
  'ESCORT': { start: '08:00', end: '20:00', label: 'Escort (8:00-20:00)' },
};

// Unavailability reasons (from StaffAvailability)
const UNAVAILABILITY_REASONS = [
  'Annual Leave',
  'Adoptive Leave',
  'Bereavement Leave',
  'Carers Leave.',
  'Certified Sick Leave',
  'Force Majeure',
  'Health and Safety Leave',
  'Jury Leave',
  'Maternity Leave',
  'Other',
  'Parents Leave',
  'Parental Leave',
  'Paternity Leave',
  'Sick Leave',
  'Study Leave / Mandatory Training',
];

// Color coding for different shift statuses
const SHIFT_STATUS_COLORS = {
  assigned: '#4caf50',
  available: '#2196f3',
  weekendAssigned: '#ff9800',
  weekendAvailable: '#ffb74d',
  agency: '#9c27b0',
  cancelled: '#f44336',
  sickLeave: '#ff5252',
  sickLeavePartial: '#ff9800',
};


// A shift is "Approved" once a manager/admin has updated the actuals and marked it Complete
const isShiftApproved = (shift) => {
  return shift?.shiftData?.actualStatus === 'Complete';
};

// A shift is "pending signoff" once its end time has passed but nobody has yet recorded what
// actually happened (actualStatus is still neither 'Complete' nor 'Not-Worked'). Mirrors the
// same logic used in the ward weekly view — only meaningful for shifts that were assigned,
// and not for shifts already flagged Cancelled through the release flow.
const isShiftPendingApproval = (shift) => {
  if (shift?.isCancelled) return false;
  const endDateTime = shift?.shiftData?.endDateTime;
  if (!endDateTime) return false;
  if (new Date(endDateTime) > new Date()) return false;
  const actualStatus = shift?.shiftData?.actualStatus;
  return actualStatus !== 'Complete' && actualStatus !== 'Not-Worked';
};



// Helper function to format hours and minutes
const formatHoursMinutes = (totalMinutes) => {
  if (!totalMinutes || totalMinutes === 0) return '0h 0m';
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  if (hours === 0) return `${minutes}m`;
  if (minutes === 0) return `${hours}h`;
  return `${hours}h ${minutes}m`;
};

// Helper function to format time
const formatTime = (dateTime) => {
  if (!dateTime) return '';
  const date = new Date(dateTime);
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

// Helper function to format date
const formatDate = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
};

// Helper function to compare objects deeply
const isEqual = (obj1, obj2) => {
  return JSON.stringify(obj1) === JSON.stringify(obj2);
};

// ─── Helper: does an availability's times overlap any assigned shift? ─────────
const isAvailabilityCoveredByShift = (availability, shiftsOnDate) => {
  if (!shiftsOnDate || shiftsOnDate.length === 0) return false;
  const availStart = new Date(availability.startDateTime);
  const availEnd   = new Date(availability.endDateTime);
  return shiftsOnDate.some(shift => {
    if (!shift.shiftData?.startDateTime) return false;
    const sStart = new Date(shift.shiftData.startDateTime);
    const sEnd   = new Date(shift.shiftData.endDateTime);
    return sStart <= availStart && sEnd >= availEnd;
  });
};

// ─── Helper: does a staff member have at least one TRUE, still-open availability entry
// (not unavailable/sick, and not already matched to an assigned shift) on a given date? ───
const hasAvailabilityOnDate = (staffAvailabilitiesForStaff, staffShiftsForStaff, dateString) => {
  const entries = staffAvailabilitiesForStaff?.[dateString] || [];
  const shiftsOnDate = staffShiftsForStaff?.[dateString] || [];
  return entries.some(
    (a) => !a.isUnavailable && !a.isSickLeave && !isAvailabilityCoveredByShift(a, shiftsOnDate)
  );
};

const StaffWeeklyView = () => {
  const { setFluid } = useLayout();
  const navigate = useNavigate();
  const location = useLocation();
  const { userInfo } = useSelector((state) => state.auth);

  // Existing state
  const [selectedWeek, setSelectedWeek] = useState(new Date());
  const [staffList, setStaffList] = useState([]);
  const [filteredStaffList, setFilteredStaffList] = useState([]);
  const [availableShifts, setAvailableShifts] = useState([]);
  const [filteredAvailableShifts, setFilteredAvailableShifts] = useState([]);
  const [staffShifts, setStaffShifts] = useState({});
  const [staffAvailabilities, setStaffAvailabilities] = useState({});
  const [draggedItem, setDraggedItem] = useState(null);
  const [shiftToRelease, setShiftToRelease] = useState(null);
  const [shiftToEdit, setShiftToEdit] = useState(null);
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [selectedStaffForEmail, setSelectedStaffForEmail] = useState(null);
  const [emailContent, setEmailContent] = useState('');
  const [emailSubject, setEmailSubject] = useState('');
  const [emailMethod, setEmailMethod] = useState('outlook');
  const [includeWeekend, setIncludeWeekend] = useState(false);
  const [releaseDialogOpen, setReleaseDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('basic');
  const [onlyShowAvailableShifts, setOnlyShowAvailableShifts] = useState(false);
  const [selectedWardFilter, setSelectedWardFilter] = useState('all');
  const [selectedJobTitleFilter, setSelectedJobTitleFilter] = useState('all');
  const [availableWards, setAvailableWards] = useState([]);
  const [availableJobTitles, setAvailableJobTitles] = useState([]);
  const [selectedStaffFilter, setSelectedStaffFilter] = useState('all');
  // Filter staff down to those with availability on a specific day of the currently-viewed week.
  // Value is a date string ('YYYY-MM-DD') matching one of weekInfo.days, or 'all'.
  const [selectedDayFilter, setSelectedDayFilter] = useState('all');
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  // NEW: Availability management state (from StaffAvailability)
  const [selectedPreset, setSelectedPreset] = useState('');
  const [editMode, setEditMode] = useState(false);
  const [editingAvailability, setEditingAvailability] = useState(null);
  const [availabilityDialogOpen, setAvailabilityDialogOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [selectedDayForAvailability, setSelectedDayForAvailability] = useState(null);
  const [selectedStaffForAvailability, setSelectedStaffForAvailability] = useState(null);

  // ============= MANAGER DIRECTORATE FILTERING =============
  // Get the manager's directorate from userInfo
  const managerDirectorate = userInfo?.directorateName || null;

  const managerOnlyOwnWard = userInfo?.onlyOwnWard === true; 
  
  // Check if user is a manager
  const isManager = userInfo?.isManager === true;
  const isAdmin = userInfo?.isAdmin === true;

  // Admin-configured ward area inclusions — additional wards a manager can
  // see regardless of their own directorate. Comes through on userInfo from
  // the login/verifyOTP response (populated HospitalLocation objects).
  const managerWardInclusions = userInfo?.wardAreaInclusions || [];
  const inclusionIds = useMemo(
    () =>
      new Set(
        managerWardInclusions.map((w) =>
          typeof w === 'object' && w !== null ? w._id : w
        )
      ),
    [managerWardInclusions]
  );

  // NOTE: directorate strings in HospitalLocation are free-text and have
  // been observed with trailing/leading whitespace (e.g. "Medical 2
  // Directorate " vs "Medical 2 Directorate") due to manual admin edits.
  // Normalize both sides before comparing so a stray space doesn't silently
  // drop a manager's own directorate wards from their view.
  const normalizeDirectorate = (val) => (val || '').trim().toLowerCase();
  const normalizedManagerDirectorate = useMemo(
    () => normalizeDirectorate(managerDirectorate),
    [managerDirectorate]
  );
  
  console.log('User Info:', { 
    isAdmin, 
    isManager, 
    directorate: managerDirectorate,
    wardInclusions: inclusionIds.size
  });
  
  // Fetch hospital locations for directorate filtering
  const { data: hospitalLocations = [], isLoading: loadingLocations } = useGetHospitalLocationsQuery();
  
  // Filter hospital locations based on manager's directorate, PLUS any
  // admin-configured ward area inclusions (extra wards outside their own
  // directorate that the admin has explicitly granted them visibility on).
  const filteredHospitalLocations = useMemo(() => {
  if (!hospitalLocations || hospitalLocations.length === 0) return [];

  if (isAdmin) return hospitalLocations;

  if (isManager && managerOnlyOwnWard) {
    return hospitalLocations.filter(
      location =>
        location.wardDepartmentArea === userInfo?.speciality ||
        inclusionIds.has(location._id)
    );
  }

  if (isManager && managerDirectorate) {
    return hospitalLocations.filter(
      location =>
        normalizeDirectorate(location.directorate) === normalizedManagerDirectorate ||
        inclusionIds.has(location._id)
    );
  }

  if (isManager && !managerDirectorate && inclusionIds.size > 0) {
    return hospitalLocations.filter(location => inclusionIds.has(location._id));
  }

  return hospitalLocations;
}, [
  hospitalLocations, isAdmin, isManager, managerOnlyOwnWard,
  managerDirectorate, normalizedManagerDirectorate, inclusionIds, userInfo?.speciality
]);
  
  // Extract ward names from filtered hospital locations
  const filteredWardNames = useMemo(() => {
    return filteredHospitalLocations.map(location => location.wardDepartmentArea).filter(Boolean);
  }, [filteredHospitalLocations]);
  
  console.log('Filtered ward names:', filteredWardNames);
  // ============= END MANAGER DIRECTORATE FILTERING =============

  // NEW: Form state (from StaffAvailability)
  const [formData, setFormData] = useState({
    startDateTime: '',
    endDateTime: '',
    comments: '',
    categoryOfStaff: '',
    wardDepartmentArea: '',
    isUnavailable: false,
    unavailabilityReason: '',
    unavailabilityType: 'Full Day',
    unavailabilityDetails: '',
    isSickLeave: false,
    sickLeaveType: 'Full Day',
    sicknessReason: '',
    requestedBy: '',
    requestedByName: '',
    requestedByEmail: '',
    createdByManager: undefined
  });

  // Existing edit form state
  const [editForm, setEditForm] = useState({
    startDateTime: '',
    endDateTime: '',
    wardDepartmentArea: '',
    categoryOfStaff: '',
    reasonForRequest: '',
    specialRequirements: '',
    comments: '',
  });

  // Calculate week dates
  const getWeekDates = (date) => {
  const current = new Date(date);
  current.setHours(0, 0, 0, 0);
  const day = current.getDay();
  const diff = current.getDate() - day + (day === 0 ? -6 : 1);

  const monday = new Date(current);
  monday.setDate(diff);
  monday.setHours(0, 0, 0, 0);

  const sunday = new Date(monday);
  sunday.setDate(monday.getDate() + 6);
  sunday.setHours(23, 59, 59, 999);

  // Build a local YYYY-MM-DD string without going via UTC
  const toLocalISO = (d) => {
    const yyyy = d.getFullYear();
    const mm   = String(d.getMonth() + 1).padStart(2, '0');
    const dd   = String(d.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  };

  return {
    startDate:   toLocalISO(monday),
    endDate:     toLocalISO(sunday),
    fullEndDate: sunday.toISOString(),   // backend query, UTC is fine here
    days: Array.from({ length: 7 }, (_, i) => {
      const d = new Date(monday);
      d.setDate(monday.getDate() + i);
      const dayOfWeek = d.getDay();
      return {
        date:      toLocalISO(d),        // ← was d.toISOString().split('T')[0]
        dayName:   ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'][dayOfWeek],
        shortName: ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'][dayOfWeek],
        isWeekend: dayOfWeek === 0 || dayOfWeek === 6
      };
    })
  };
};

  const weekInfo = useMemo(() => getWeekDates(selectedWeek), [selectedWeek]);

  // Determine access type based on user role
  const accessType = useMemo(() => {
    if (userInfo?.isAdmin) return 'Admin';
    if (userInfo?.isManager) return 'Manager';
    return 'User';
  }, [userInfo]);

  // Parse query parameters
  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const weekParam = queryParams.get('week');
    if (weekParam) {
      try {
        const date = new Date(weekParam);
        if (!isNaN(date.getTime())) {
          setSelectedWeek(date);
        }
      } catch (error) {
        console.error('Invalid date in URL:', error);
      }
    }
  }, [location]);

  // Fetch data with access type
  const { data: staffData, isLoading: loadingStaff, refetch: refetchStaff } = useGetUsersQuery();
  const { data: shiftsData, isLoading: loadingShifts, refetch: refetchShifts } = useGetWeeklyShiftsByStaffQuery({
    startDate: weekInfo.startDate,
    endDate: weekInfo.fullEndDate,
    accessType // Pass access type to API
  });
  
  const { data: availableShiftsData, refetch: refetchAvailable } = useGetAvailableShiftsQuery({
    startDate: weekInfo.startDate,
    endDate: weekInfo.fullEndDate,
    accessType // Pass access type to API
  });
  
  const { data: availabilitiesData, refetch: refetchAvailabilities } = useGetWeeklyAvailabilitiesByStaffQuery({
    startDate: weekInfo.startDate,
    endDate: weekInfo.fullEndDate
  });

  // Mutations
  const [assignStaff] = useAssignStaffToShiftMutation();
  const [reOpenShift] = useReOpenShiftMutation();
  const [updateShift] = useUpdateShiftMutation();
  const [createAvailability] = useCreateAvailabilityMutation();
  const [updateAvailability] = useUpdateAvailabilityMutation();
  const [deleteAvailability] = useDeleteAvailabilityMutation();

  // Layout effect
  useEffect(() => {
    setFluid(true);
    return () => {
      setFluid(false);
    };
  }, [setFluid]);

  // Process staff data
useEffect(() => {
  if (staffData) {
    const filteredStaff = staffData
      .filter(user =>
        !user.isAdmin &&
        !user.isAgency &&
        user.isActive !== false &&
        (isAdmin || !isAdminOnlyJobTitle(user.jobtitle))
      )
      .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    setStaffList(filteredStaff);
    setFilteredStaffList(filteredStaff);

    const jobTitles = [...new Set(filteredStaff
      .map(staff => staff.jobtitle)
      .filter(title => title && title.trim() !== '')
    )].sort();
    setAvailableJobTitles(['all', ...jobTitles]);
  }
}, [staffData, isAdmin]);

  // Helper to sort shifts chronologically by start time (earliest first) —
  // matches the ordering managers see on the admin-side shift screens.
  const sortShiftsChronologically = (shifts) => {
    return [...shifts].sort((a, b) => {
      const aTime = new Date(a.startDateTime).getTime();
      const bTime = new Date(b.startDateTime).getTime();
      return aTime - bTime;
    });
  };

  // Process available shifts data - FILTER BY DATE (today onwards), THEN DIRECTORATE (+ inclusions), THEN SORT CHRONOLOGICALLY
  useEffect(() => {
    if (availableShiftsData) {
      // Only show shifts from today onwards
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const shiftsFromToday = availableShiftsData.filter(shift => new Date(shift.startDateTime) >= today);

      // Then filter shifts by manager's directorate (or inclusion list) if applicable
      let filteredShiftsByDirectorate = shiftsFromToday;
      
      if (isManager && !isAdmin && filteredWardNames.length > 0) {
        filteredShiftsByDirectorate = shiftsFromToday.filter(shift => 
          filteredWardNames.includes(shift.wardDepartmentArea)
        );
        console.log(`Filtered available shifts by directorate + inclusions: ${filteredShiftsByDirectorate.length} of ${shiftsFromToday.length}`);
      }
      
      // Sort chronologically (earliest shift first) before anything else
      const sortedByDirectorate = sortShiftsChronologically(filteredShiftsByDirectorate);
      
      if (!isEqual(availableShifts, sortedByDirectorate)) {
        setAvailableShifts(sortedByDirectorate);
      }
      
      // Apply ward filter (still chronological, since we sorted before filtering further)
      let filteredShifts = sortedByDirectorate;
      if (selectedWardFilter !== 'all') {
        filteredShifts = filteredShifts.filter(shift => 
          shift.wardDepartmentArea === selectedWardFilter
        );
      }

      // Apply ward exclusions for the selected staff member, layered on top of
      // the directorate/inclusion + ward filtering above (still chronological)
      if (selectedStaffFilter !== 'all') {
        const selectedStaffForExclusions = staffList.find(s => s._id === selectedStaffFilter);
        if (selectedStaffForExclusions?.wardExclusions?.length > 0) {
          filteredShifts = filteredShifts.filter(shift =>
            !selectedStaffForExclusions.wardExclusions.some(
              ward => ward.toLowerCase() === (shift.wardDepartmentArea || '').toLowerCase()
            )
          );
        }
      }
      
      if (!isEqual(filteredAvailableShifts, filteredShifts)) {
        setFilteredAvailableShifts(filteredShifts);
      }
      
      // Get unique wards from directorate-filtered shifts
      const wards = [...new Set(filteredShiftsByDirectorate
        .map(shift => shift.wardDepartmentArea)
        .filter(ward => ward && ward.trim() !== '')
      )].sort();
      
      if (!isEqual(availableWards, ['all', ...wards])) {
        setAvailableWards(['all', ...wards]);
      }
    }
  }, [availableShiftsData, selectedWardFilter, selectedStaffFilter, staffList, isManager, isAdmin, filteredWardNames]);

  // Process staff availabilities
  useEffect(() => {
    if (availabilitiesData && staffList.length > 0) {
      const availabilitiesByStaff = {};
      staffList.forEach(staff => {
        availabilitiesByStaff[staff._id] = {};
        weekInfo.days.forEach(day => {
          availabilitiesByStaff[staff._id][day.date] = [];
        });
      });
      
      availabilitiesData.forEach(availability => {
        if (!availability.requestedBy) return;
        
        let staffId;
        if (typeof availability.requestedBy === 'object') {
          staffId = availability.requestedBy._id;
        } else {
          staffId = availability.requestedBy;
        }
        
        if (!staffId || !availabilitiesByStaff[staffId]) return;
        
        const availabilityStart = new Date(availability.startDateTime);
        const availabilityEnd = new Date(availability.endDateTime);
        //const dateString = availabilityStart.toISOString().split('T')[0];
        const dateString = getLocalDateString(availabilityStart.toISOString());

        if (availabilitiesByStaff[staffId][dateString] !== undefined) {
          const dayOfWeek = availabilityStart.getDay();
          const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
          
          // Check if this is actually an unavailability record
          const isUnavailableRecord = availability.isUnavailable || availability.isSickLeave;
          
          availabilitiesByStaff[staffId][dateString].push({
            id: availability._id,
            startDateTime: availability.startDateTime,
            endDateTime: availability.endDateTime,
            startTime: availabilityStart.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
            endTime: availabilityEnd.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
            durationMinutes: availability.durationMinutes,
            comments: availability.comments,
            status: availability.status || 'Pending',
            isWeekend: isWeekend,
            categoryOfStaff: availability.categoryOfStaff || 'General',
            ward: availability.wardDepartmentArea || '',
            location: availability.hospitalLocationId?.name || '',
            
            // UNVAILABILITY FIELDS
            isSickLeave: availability.isSickLeave || false,
            sickLeaveType: availability.sickLeaveType || 'Full Day',
            sicknessReason: availability.sicknessReason || '',
            
            isUnavailable: availability.isUnavailable || false,
            unavailabilityReason: availability.unavailabilityReason || '',
            unavailabilityType: availability.unavailabilityType || 'Full Day',
            unavailabilityDetails: availability.unavailabilityDetails || '',
            
            isActuallyUnavailable: isUnavailableRecord,
            
            createdByManager: availability.createdByManager
          });
        }
      });
      
      if (!isEqual(staffAvailabilities, availabilitiesByStaff)) {
        setStaffAvailabilities(availabilitiesByStaff);
      }
    }
  }, [availabilitiesData, staffList, weekInfo.days]);

  // Apply filters
  useEffect(() => {
    let filteredStaff = [...staffList];
    
    if (selectedJobTitleFilter !== 'all') {
      filteredStaff = filteredStaff.filter(staff => 
        staff.jobtitle === selectedJobTitleFilter
      );
    }

    if (selectedStaffFilter !== 'all') {
      filteredStaff = filteredStaff.filter(staff => staff._id === selectedStaffFilter);
    }

    if (selectedDayFilter !== 'all') {
      filteredStaff = filteredStaff.filter(staff =>
        hasAvailabilityOnDate(staffAvailabilities[staff._id], staffShifts[staff._id], selectedDayFilter)
      );
    }

    filteredStaff.sort((a, b) => (a.name || '').localeCompare(b.name || ''));

    if (!isEqual(filteredStaffList, filteredStaff)) {
      setFilteredStaffList(filteredStaff);
    }
  }, [selectedJobTitleFilter, selectedStaffFilter, selectedDayFilter, staffList, staffAvailabilities, staffShifts]);

  // Process staff shifts
  useEffect(() => {
    if (shiftsData && staffList.length > 0) {
      const shiftsByStaff = {};
      staffList.forEach(staff => {
        shiftsByStaff[staff._id] = {};
        weekInfo.days.forEach(day => {
          shiftsByStaff[staff._id][day.date] = [];
        });
      });
      
      shiftsData.forEach(shift => {
        if (shift.filledBy) {
          const staffId = shift.filledBy._id || shift.filledBy;
         // const shiftDate = new Date(shift.startDateTime).toISOString().split('T')[0];
          const shiftDate = getLocalDateString(shift.startDateTime);
          
          if (shiftsByStaff[staffId] && shiftsByStaff[staffId][shiftDate]) {
            const dayOfWeek = new Date(shift.startDateTime).getDay();
            const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
            const isAgency = !!shift.filledByAgency;
            const isCancelled = shift.lineStatus === 'Cancelled';
            
            shiftsByStaff[staffId][shiftDate].push({
              id: shift._id,
              startTime: new Date(shift.startDateTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
              endTime: new Date(shift.endDateTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
              ward: shift.wardDepartmentArea,
              location: shift.hospitalLocationId?.name,
              categoryOfStaff: shift.categoryOfStaff,
              isWeekendShift: isWeekend,
              isAgencyFilled: isAgency,
              isCancelled: isCancelled,
              lineStatus: shift.lineStatus,
              shiftData: shift
            });
          }
        }
      });
      
      if (!isEqual(staffShifts, shiftsByStaff)) {
        setStaffShifts(shiftsByStaff);
      }
    }
  }, [shiftsData, staffList, weekInfo.days]);

  const generateSchedulePlainText = (staffId) => {
    const staff = staffList.find(s => s._id === staffId);
    if (!staff) return '';
    
    const emailDays = getEmailDays();
    
    let text = `WEEKLY SCHEDULE\n`;
    text += `===============\n\n`;
    text += `Staff: ${staff.name}\n`;
    text += `Week: ${formatDateRange()}\n`;
    text += `Job Title: ${staff.jobtitle || 'Not specified'}\n`;
    text += `Speciality: ${staff.speciality || 'General'}\n`;
    text += `Generated: ${new Date().toLocaleDateString()}\n\n`;
    text += `='.repeat(60)}\n\n`;
    
    let hasShifts = false;
    
    emailDays.forEach(day => {
      const shifts = staffShifts[staffId]?.[day.date] || [];
      const dayHasShifts = shifts.length > 0;
      
      if (dayHasShifts) hasShifts = true;
      
      text += `${day.dayName.toUpperCase()} (${day.date})`;
      if (day.isWeekend) text += ' [WEEKEND]';
      text += '\n';
      text += '-'.repeat(40) + '\n';
      
      if (dayHasShifts) {
        shifts.forEach((shift, index) => {
          text += `${index + 1}. ${shift.startTime} - ${shift.endTime}\n`;
          text += `   Ward: ${shift.ward}\n`;
          text += `   Location: ${shift.location || 'Not specified'}\n`;
          text += `   Category: ${shift.categoryOfStaff}\n`;
          if (shift.isWeekendShift) text += `   * Weekend Shift *\n`;
          if (shift.isAgencyFilled) text += `   * Agency Staff *\n`;
          text += '\n';
        });
      } else {
        text += `No shifts scheduled\n\n`;
      }
      
      text += '\n';
    });
    
    if (!hasShifts) {
      text += `\nNO SHIFTS SCHEDULED\n`;
      text += `You have no assigned shifts from ${formatDateRange()}\n\n`;
    }
    
    text += `\nIMPORTANT NOTES:\n`;
    text += `- This schedule is subject to change based on operational needs\n`;
    text += `- Please arrive 15 minutes before your scheduled start time\n`;
    text += `- Contact your manager if you have any questions or need to make changes\n`;
    text += `- Weekend shifts are highlighted\n\n`;
    text += `Beaumont Hospital Staff Scheduling System\n`;
    text += `This schedule was automatically generated.\n`;
    text += `For questions, contact: beaumontNursingBank@beaumont.ie\n`;
    
    return text;
  };

  const openOutlookEmail = (staffId, staffEmail, subject, body) => {
    const mailtoLink = `mailto:${staffEmail}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    
    window.location.href = mailtoLink;
    
    setTimeout(() => {
      if (window.location.href.includes('mailto:')) {
        showSnackbar('Outlook not detected. Using web email instead.', 'info');
        setEmailMethod('web');
        setEmailDialogOpen(true);
      }
    }, 500);
  };

  // Debug function to check availability data
  // Debug function to check availability data
const debugAvailabilityData = () => {
  // console.log('=== AVAILABILITY DATA DEBUG ===');
  
  filteredStaffList.forEach(staff => {
    const staffAvailabilitiesData = staffAvailabilities[staff._id] || {};
    
    Object.entries(staffAvailabilitiesData).forEach(([date, availabilities]) => {
      availabilities.forEach((availability, idx) => {
        if (availability.isUnavailable || availability.isSickLeave) {
          // console.log(`🔴 UNAVAILABLE FOUND:`, {
          //   staff: staff.name,
          //   date: date,
          //   startTime: availability.startTime,
          //   endTime: availability.endTime,
          //   isUnavailable: availability.isUnavailable,
          //   isSickLeave: availability.isSickLeave,
          //   reason: availability.unavailabilityReason || availability.sicknessReason,
          //   unavailabilityType: availability.unavailabilityType,
          //   sickLeaveType: availability.sickLeaveType
          // });
        } else {
          // console.log(`🟢 AVAILABLE:`, {
          //   staff: staff.name,
          //   date: date,
          //   startTime: availability.startTime,
          //   endTime: availability.endTime,
          //   status: availability.status
          // });
        }
      });
    });
  });
  
  // console.log('=== END DEBUG ===');
};

  const handleSendWebEmail = async () => {
    if (!selectedStaffForEmail) return;
    
    try {
      const htmlContent = generateScheduleHTMLTable(selectedStaffForEmail.id);
      const plainTextContent = generateSchedulePlainText(selectedStaffForEmail.id);
      
      const emailData = {
        to: selectedStaffForEmail.email,
        subject: emailSubject,
        text: plainTextContent + '\n\n' + emailContent,
        html: htmlContent + '<hr><p><em>Additional Notes:</em></p><p>' + emailContent.replace(/\n/g, '<br>') + '</p>'
      };
      
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      showSnackbar(`Email sent to ${selectedStaffForEmail.name} via web email`, 'success');
      setEmailDialogOpen(false);
      
    } catch (error) {
      console.error('Failed to send email:', error);
      showSnackbar('Failed to send email', 'error');
    }
  };

  const generateScheduleHTMLTable = (staffId) => {
    const staff = staffList.find(s => s._id === staffId);
    if (!staff) return '';
    
    const emailDays = getEmailDays();
    
    let html = `
      <div style="font-family: Arial, sans-serif; max-width: 800px; margin: 0 auto;">
        <h2 style="color: #2c3e50; border-bottom: 2px solid #3498db; padding-bottom: 10px;">
          Weekly Schedule for ${staff.name}
        </h2>
        
        <div style="background-color: #f8f9fa; padding: 15px; border-radius: 5px; margin-bottom: 20px;">
          <table style="width: 100%; border-collapse: collapse;">
            <tr>
              <td style="padding: 5px;"><strong>Week:</strong> ${formatDateRange()}</td>
              <td style="padding: 5px;"><strong>Job Title:</strong> ${staff.jobtitle || 'Not specified'}</td>
            </tr>
            <tr>
              <td style="padding: 5px;"><strong>Speciality:</strong> ${staff.speciality || 'General'}</td>
              <td style="padding: 5px;"><strong>Generated:</strong> ${new Date().toLocaleDateString()}</td>
            </tr>
          </table>
        </div>
        
        <div style="overflow-x: auto;">
          <table style="width: 100%; border-collapse: collapse; border: 1px solid #ddd;">
            <thead>
              <tr style="background-color: #3498db; color: white;">
                <th style="padding: 12px; text-align: left; width: 15%;">Day</th>
                <th style="padding: 12px; text-align: left; width: 85%;">Shifts</th>
              </tr>
            </thead>
            <tbody>
    `;
    
    let hasShifts = false;
    
    emailDays.forEach((day, dayIndex) => {
      const shifts = staffShifts[staffId]?.[day.date] || [];
      const dayHasShifts = shifts.length > 0;
      
      if (dayHasShifts) hasShifts = true;
      
      html += `
        <tr style="${dayIndex % 2 === 0 ? 'background-color: #f9f9f9;' : 'background-color: white;'}">
          <td style="padding: 10px; border: 1px solid #ddd; vertical-align: top;">
            <div style="font-weight: bold; color: ${day.isWeekend ? '#e67e22' : '#2c3e50'}">
              ${day.dayName}<br>
              <span style="font-size: 12px; color: #7f8c8d;">${day.date}</span>
            </div>
            ${day.isWeekend ? '<div style="font-size: 11px; color: #e67e22; font-style: italic;">Weekend</div>' : ''}
          </td>
          <td style="padding: 10px; border: 1px solid #ddd;">
      `;
      
      if (dayHasShifts) {
        shifts.forEach((shift, shiftIndex) => {
          const shiftColor = shift.isWeekendShift ? '#e67e22' : '#2ecc71';
          html += `
            <div style="
              background-color: ${shiftColor}10;
              border-left: 4px solid ${shiftColor};
              padding: 8px;
              margin-bottom: 8px;
              border-radius: 4px;
            ">
              <table style="width: 100%;">
                <tr>
                  <td style="width: 30%;">
                    <strong style="color: ${shiftColor};">${shift.startTime} - ${shift.endTime}</strong>
                  </td>
                  <td style="width: 35%;">
                    <strong>Ward:</strong> ${shift.ward}
                  </td>
                  <td style="width: 35%;">
                    <strong>Category:</strong> ${shift.categoryOfStaff}
                  </td>
                </tr>
                <tr>
                  <td colspan="3" style="padding-top: 4px;">
                    <strong>Location:</strong> ${shift.location || 'Not specified'}
                    ${shift.isWeekendShift ? '<span style="color: #e67e22; margin-left: 10px;">• Weekend Shift</span>' : ''}
                    ${shift.isAgencyFilled ? '<span style="color: #9c27b0; margin-left: 10px;">• Agency Staff</span>' : ''}
                  </td>
                </tr>
              </table>
            </div>
          `;
        });
      } else {
        html += `
          <div style="
            background-color: #f5f5f5;
            padding: 12px;
            text-align: center;
            color: #7f8c8d;
            font-style: italic;
            border-radius: 4px;
          ">
            No shifts scheduled
          </div>
        `;
      }
      
      html += `
          </td>
        </tr>
      `;
    });
    
    if (!hasShifts) {
      html += `
        <tr>
          <td colspan="2" style="padding: 30px; text-align: center; color: #7f8c8d;">
            <div style="font-size: 16px; margin-bottom: 10px;">No shifts scheduled for this week</div>
            <div style="font-size: 14px;">You have no assigned shifts from ${formatDateRange()}</div>
          </td>
        </tr>
      `;
    }
    
    html += `
            </tbody>
          </table>
        </div>
        
        <div style="margin-top: 30px; padding: 15px; background-color: #f8f9fa; border-radius: 5px;">
          <h3 style="color: #2c3e50; margin-top: 0;">Important Notes</h3>
          <ul style="color: #555;">
            <li>This schedule is subject to change based on operational needs</li>
            <li>Please arrive 15 minutes before your scheduled start time</li>
            <li>Contact your manager if you have any questions or need to make changes</li>
            <li>Weekend shifts are highlighted in orange</li>
          </ul>
        </div>
        
        <div style="margin-top: 30px; padding-top: 20px; border-top: 1px solid #ddd; color: #7f8c8d; font-size: 12px;">
          <p>
            <strong>Beaumont Hospital Staff Scheduling System</strong><br>
            This schedule was automatically generated. Please do not reply to this email.<br>
            For questions, contact: beaumontNursingBank@beaumont.ie
          </p>
        </div>
      </div>
    `;
    
    return html;
  };

  const getEmailDays = () => {
    return weekInfo.days.filter(day => includeWeekend || !day.isWeekend);
  };

  const handlePresetSelect = (presetKey) => {
    const preset = SHIFT_PRESETS[presetKey];
    if (preset) {
      setEditForm(prev => ({
        ...prev,
        selectedPreset: presetKey,
        startTime: preset.start,
        endTime: preset.end,
        escort: presetKey === 'ESCORT'
      }));
    }
  };

  const calculateShiftDuration = () => {
    if (!editForm.startDate || !editForm.startTime || !editForm.endDate || !editForm.endTime) {
      return 'Please enter dates and times';
    }
    
    const startDateTime = new Date(`${editForm.startDate}T${editForm.startTime}`);
    let endDateTime = new Date(`${editForm.endDate}T${editForm.endTime}`);
    
    if (editForm.endTime < editForm.startTime && editForm.startDate === editForm.endDate) {
      endDateTime.setDate(endDateTime.getDate() + 1);
    }
    
    if (endDateTime <= startDateTime) {
      return 'Invalid: End time must be after start time';
    }
    
    const durationMs = endDateTime - startDateTime;
    const durationHours = Math.floor(durationMs / (1000 * 60 * 60));
    const durationMinutes = Math.floor((durationMs % (1000 * 60 * 60)) / (1000 * 60));
    
    return `${durationHours} hours ${durationMinutes} minutes`;
  };

  // Get shift background color
  const getShiftColor = (shift) => {
    if (shift.isCancelled) return SHIFT_STATUS_COLORS.cancelled;
    if (shift.isAgencyFilled) return SHIFT_STATUS_COLORS.agency;
    if (shift.isWeekendShift) return SHIFT_STATUS_COLORS.weekendAssigned;
    return SHIFT_STATUS_COLORS.assigned;
  };

  // Get availability background color
  const getAvailabilityColor = (status) => {
    switch(status) {
      case 'Approved': return '#e8f5e8';
      case 'Rejected': return '#ffebee';
      default: return '#e3f2fd';
    }
  };

  // Get availability border color
  const getAvailabilityBorderColor = (status) => {
    switch(status) {
      case 'Approved': return '#4caf50';
      case 'Rejected': return '#f44336';
      default: return '#2196f3';
    }
  };

  // NEW: Helper functions from StaffAvailability
  const formatDateTimeLocal = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  // NEW: Handle preset selection
  const handlePresetChange = (presetKey) => {
  setSelectedPreset(presetKey);
  if (presetKey && SHIFT_PRESETS[presetKey] && formData.startDateTime) {
    const preset = SHIFT_PRESETS[presetKey];
    const datePart = formData.startDateTime.split('T')[0];
    const [startH] = preset.start.split(':').map(Number);
    const [endH] = preset.end.split(':').map(Number);
    const crossesMidnight = endH < startH;
    const addOneDay = (dateStr) => {
      const [y,m,d] = dateStr.split('-').map(Number);
      const dt = new Date(Date.UTC(y,m-1,d));
      dt.setUTCDate(dt.getUTCDate()+1);
      return dt.toISOString().split('T')[0];
    };
    setFormData(prev => ({
      ...prev,
      startDateTime: `${datePart}T${preset.start}`,
      endDateTime: crossesMidnight
        ? `${addOneDay(datePart)}T${preset.end}`
        : `${datePart}T${preset.end}`
    }));
  }
};

  // NEW: Handle start date change
  const handleStartDateChange = (e) => {
  const { value } = e.target;
  const [datePart, timePart] = value.split('T');
  const [startH, startM] = timePart.split(':').map(Number);

  setFormData(prev => {
    let endStr;
    if (!prev.endDateTime || prev.endDateTime <= value) {
      // Auto-set end 8 hours later using pure string arithmetic
      const endH = startH + 8;
      if (endH >= 24) {
        const [y, mo, d] = datePart.split('-').map(Number);
        const next = new Date(Date.UTC(y, mo - 1, d));
        next.setUTCDate(next.getUTCDate() + 1);
        const nextStr = next.toISOString().split('T')[0];
        endStr = `${nextStr}T${String(endH - 24).padStart(2, '0')}:${String(startM).padStart(2, '0')}`;
      } else {
        endStr = `${datePart}T${String(endH).padStart(2, '0')}:${String(startM).padStart(2, '0')}`;
      }
    } else {
      // Keep existing end time, just update the date portion
      const endTimePart = prev.endDateTime.split('T')[1];
      endStr = `${datePart}T${endTimePart}`;
    }
    return { ...prev, startDateTime: value, endDateTime: endStr };
  });

  setSelectedPreset('');
};

  // NEW: Handle form changes
  const handleFormChange = (e) => {
    const { name, value } = e.target;
    if (name === 'startDateTime') {
      handleStartDateChange(e);
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  // NEW: Handle end date change
  const handleEndDateChange = (e) => {
    const { value } = e.target;
    setFormData(prev => ({
      ...prev,
      endDateTime: value
    }));
    setSelectedPreset('');
  };

  // NEW: Open availability dialog for creating
  const handleOpenCreateAvailability = (staff, day) => {
    setSelectedStaffForAvailability(staff);
    setSelectedDayForAvailability(day);
    
        
    const defaultStartStr = `${day.date}T09:00`;
    const defaultEndStr   = `${day.date}T17:00`;
    
    
    
    setFormData({
      startDateTime: defaultStartStr,
      endDateTime: defaultEndStr,
      comments: '',
      categoryOfStaff: staff.jobtitle || '',
      wardDepartmentArea: staff.ward || '',
      isUnavailable: false,
      unavailabilityReason: '',
      unavailabilityType: 'Full Day',
      unavailabilityDetails: '',
      isSickLeave: false,
      sickLeaveType: 'Full Day',
      sicknessReason: '',
      requestedBy: staff._id,
      requestedByName: staff.name,
      requestedByEmail: staff.email,
      createdByManager: (userInfo.isAdmin || userInfo.isManager) ? {
        id: userInfo._id,
        name: userInfo.name,
        email: userInfo.email
      } : undefined
    });
    
    setEditMode(false);
    setEditingAvailability(null);
    setSelectedPreset('');
    setAvailabilityDialogOpen(true);
  };

  // NEW: Open availability dialog for editing
  const handleOpenEditAvailability = (availability, staff) => {
    setSelectedStaffForAvailability(staff);
    setEditingAvailability(availability);
    setEditMode(true);
    setSelectedPreset('');
    
    setFormData({
      startDateTime: dublinDateTimeInputStr(availability.startDateTime),
      endDateTime: dublinDateTimeInputStr(availability.endDateTime),
      comments: availability.comments || '',
      categoryOfStaff: staff.jobtitle || availability.categoryOfStaff || '',
      wardDepartmentArea: staff.ward || availability.wardDepartmentArea || '',
      isUnavailable: availability.isUnavailable || availability.isSickLeave || false,
      unavailabilityReason: availability.unavailabilityReason || availability.sicknessReason || '',
      unavailabilityType: availability.unavailabilityType || availability.sickLeaveType || 'Full Day',
      unavailabilityDetails: availability.unavailabilityDetails || '',
      isSickLeave: availability.isSickLeave || false,
      sickLeaveType: availability.sickLeaveType || 'Full Day',
      sicknessReason: availability.sicknessReason || '',
      requestedBy: staff._id,
      requestedByName: staff.name,
      requestedByEmail: staff.email,
      createdByManager: availability.createdByManager
    });
    
    setAvailabilityDialogOpen(true);
  };

  // NEW: Handle form submission
  const handleFormSubmit = async () => {
    try {
      if (!formData.startDateTime || !formData.endDateTime) {
        showSnackbar('Start and end times are required', 'error');
        return;
      }
      
      const [startDatePart, startTimePart] = formData.startDateTime.split('T');
      const [endDatePart, endTimePart] = formData.endDateTime.split('T');
      const startDateTimeISO = dublinLocalToUtcISO(startDatePart, startTimePart);
      const endDateTimeISO = dublinLocalToUtcISO(endDatePart, endTimePart);
      const startTime = new Date(startDateTimeISO);
      const endTime = new Date(endDateTimeISO);
      
      if (endTime <= startTime) {
        showSnackbar('End time must be after start time', 'error');
        return;
      }
      
      const durationHours = (endTime - startTime) / (1000 * 60 * 60);
      if (durationHours > 24) {
        showSnackbar('Availability cannot exceed 24 hours', 'error');
        return;
      }
      
      const formatDateForBackend = (date) => {
        const d = new Date(date);
        return d.toISOString();
      };
      
      
      
      if (userInfo.isAdmin || userInfo.isManager) {
        if (!formData.requestedBy) {
          showSnackbar('Staff information is missing', 'error');
          return;
        }
        
        const submissionData = {
          ...formData,
          startDateTime: startDateTimeISO,
          endDateTime: endDateTimeISO,
          ...(formData.isUnavailable && (userInfo.isAdmin || userInfo.isManager) && {
            status: 'Approved',
            approved: true
          }),
          createdByManager: (userInfo.isAdmin || userInfo.isManager) ? {
            id: userInfo._id,
            name: userInfo.name,
            email: userInfo.email,
            date: new Date().toISOString()
          } : undefined,
          requestedBy: formData.requestedBy,
          requestedByName: formData.requestedByName,
          requestedByEmail: formData.requestedByEmail
        };
        
        if (editMode && editingAvailability) {
          await updateAvailability({
            id: editingAvailability.id,
            ...submissionData
          }).unwrap();
          
          showSnackbar('Availability updated successfully', 'success');
        } else {
          await createAvailability(submissionData).unwrap();
          showSnackbar('Availability added successfully', 'success');
        }
      } else {
        const submissionData = {
          ...formData,
          startDateTime: startDateTimeISO,
          endDateTime: endDateTimeISO,
          requestedBy: userInfo._id,
          requestedByName: userInfo.name,
          requestedByEmail: userInfo.email
        };
        
        if (editMode && editingAvailability) {
          await updateAvailability({
            id: editingAvailability.id,
            ...submissionData
          }).unwrap();
          
          showSnackbar('Availability updated successfully', 'success');
        } else {
          await createAvailability(submissionData).unwrap();
          showSnackbar('Availability added successfully', 'success');
        }
      }
      
      setAvailabilityDialogOpen(false);
      refetchAvailabilities();
      setSelectedPreset('');
      
    } catch (error) {
      console.error('Form submit error:', error);
      showSnackbar(error?.data?.message || 'Failed to save availability', 'error');
    }
  };

  // NEW: Handle delete availability
  const handleDeleteClick = async (availabilityId, event = null) => {
    if (event) {
      event.stopPropagation();
    }
    
    if (window.confirm('Are you sure you want to remove this availability? This action can be reversed by an administrator.')) {
      try {
        await updateAvailability({
          id: availabilityId,
          isActive: false
        }).unwrap();
        
        showSnackbar('Availability removed successfully', 'success');
        refetchAvailabilities();
        
        if (editMode) {
          setAvailabilityDialogOpen(false);
          setEditMode(false);
          setEditingAvailability(null);
        }
      } catch (error) {
        showSnackbar(error?.data?.message || 'Failed to remove availability', 'error');
      }
    }
  };

  // NEW: Handle delete confirmation dialog
  const handleDeleteConfirmOpen = () => {
    setDeleteConfirmOpen(true);
  };

  const handleDeleteConfirmClose = () => {
    setDeleteConfirmOpen(false);
  };

  const handleConfirmDelete = async () => {
    if (!editingAvailability) return;
    
    try {
      await updateAvailability({
        id: editingAvailability.id,
        isActive: false
      }).unwrap();
      
      showSnackbar('Availability removed successfully', 'success');
      refetchAvailabilities();
      
      setDeleteConfirmOpen(false);
      setAvailabilityDialogOpen(false);
      setEditMode(false);
      setEditingAvailability(null);
    } catch (error) {
      showSnackbar(error?.data?.message || 'Failed to remove availability', 'error');
      setDeleteConfirmOpen(false);
    }
  };

  // NEW: Close availability dialog
  const handleCloseAvailabilityDialog = () => {
    setAvailabilityDialogOpen(false);
    setEditMode(false);
    setEditingAvailability(null);
    setSelectedPreset('');
    setSelectedStaffForAvailability(null);
    setSelectedDayForAvailability(null);
  };

  // Render availability card
  // Update the renderAvailabilityCard function with responsive styling
const renderAvailabilityCard = (availability, idx, staffId, dayDate) => {
  const staff = staffList.find(s => s._id === staffId);
  
  const isSickLeave = availability.isSickLeave || false;
  const isUnavailable = availability.isUnavailable || isSickLeave;
  
  if (isUnavailable) {
    const isPartial = availability.unavailabilityType === 'Partial Day' || 
                     availability.unavailabilityType === 'Half Day AM' || 
                     availability.unavailabilityType === 'Half Day PM' ||
                     availability.sickLeaveType === 'Partial Day' ||
                     availability.sickLeaveType === 'Half Day AM' ||
                     availability.sickLeaveType === 'Half Day PM';
    
    const reason = isSickLeave 
      ? availability.sicknessReason || 'Sick Leave'
      : availability.unavailabilityReason || 'Unavailable';
    
    const typeText = isSickLeave 
      ? availability.sickLeaveType 
      : availability.unavailabilityType;
    
    return (
      <Tooltip 
        key={`unavailable-${availability.id}-${idx}`}
        title={
          <div style={{ maxWidth: '200px' }}>
            <div><strong>UNAVAILABLE</strong></div>
            {isSickLeave && <div><strong>Type:</strong> Sick Leave</div>}
            <div><strong>Reason:</strong> {reason}</div>
            <div><strong>Duration:</strong> {typeText}</div>
            {availability.startTime && availability.endTime && (
              <div><strong>Time:</strong> {availability.startTime} - {availability.endTime}</div>
            )}
          </div>
        }
        arrow
        className="no-print"
      >
        <Card 
          sx={{ 
            mb: 0.5, 
            bgcolor: isSickLeave ? 
              (isPartial ? SHIFT_STATUS_COLORS.sickLeavePartial : SHIFT_STATUS_COLORS.sickLeave) :
              '#d32f2f',
            color: 'white',
            border: '2px solid',
            borderColor: isSickLeave ? 
              (isPartial ? '#e65100' : '#d32f2f') : 
              '#b71c1c',
            '&:hover': {
              boxShadow: 3,
              transform: 'translateY(-2px)',
              transition: 'all 0.2s',
              cursor: 'pointer'
            }
          }}
          onDoubleClick={() => handleOpenEditAvailability(availability, staff)}
          onClick={(e) => {
            if (e.ctrlKey || e.metaKey) {
              handleOpenEditAvailability(availability, staff);
            }
          }}
        >
          <CardContent sx={{ p: { xs: 0.5, sm: 1 }, position: 'relative' }}>
            <IconButton
              size="small"
              sx={{
                position: 'absolute',
                top: { xs: 0, sm: 2 },
                right: { xs: 0, sm: 2 },
                color: 'white',
                backgroundColor: 'rgba(0,0,0,0.2)',
                padding: { xs: '1px', sm: '2px' },
                '&:hover': {
                  backgroundColor: 'rgba(0,0,0,0.4)',
                }
              }}
              onClick={(e) => {
                e.stopPropagation();
                handleOpenEditAvailability(availability, staff);
              }}
              className="no-print"
            >
              <Edit fontSize="small" sx={{ fontSize: { xs: '0.7rem', sm: '0.9rem' } }} />
            </IconButton>
            
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, pr: { xs: 2, sm: 3 } }}>
              <Typography variant="body2" sx={{ fontSize: { xs: '0.6rem', sm: '0.8rem' }, fontWeight: 'bold' }}>
                {isSickLeave ? '🏥 SICK' : '⛔ UNAVAIL'}
              </Typography>
              <Chip 
                label={typeText}
                size="small"
                sx={{ 
                  height: { xs: 14, sm: 16 },
                  fontSize: { xs: '0.5rem', sm: '0.6rem' },
                  backgroundColor: 'rgba(255,255,255,0.3)',
                  color: 'white'
                }} 
              />
            </Box>
            
            {availability.startTime && availability.endTime && (
              <Typography variant="body2" sx={{ fontSize: { xs: '0.55rem', sm: '0.75rem' } }}>
                {availability.startTime} - {availability.endTime}
              </Typography>
            )}
            
            {reason && reason !== 'Unavailable' && (
              <Typography variant="caption" sx={{ fontSize: { xs: '0.5rem', sm: '0.65rem' }, display: 'block', mt: 0.5 }}>
                {reason.length > 20 ? reason.substring(0, 17) + '...' : reason}
              </Typography>
            )}
          </CardContent>
        </Card>
      </Tooltip>
    );
  }
  
  return (
    <Tooltip 
      key={`availability-${availability.id}-${idx}`}
      title={
        <div style={{ maxWidth: '200px' }}>
          <div><strong>Status:</strong> {availability.status}</div>
          <div><strong>Category:</strong> {availability.categoryOfStaff}</div>
          {availability.ward && <div><strong>Ward:</strong> {availability.ward}</div>}
          <div><strong>Duration:</strong> {formatHoursMinutes(availability.durationMinutes)}</div>
        </div>
      }
      arrow
      className="no-print"
    >
      <Card 
        sx={{ 
          mb: 0.5, 
          bgcolor: getAvailabilityColor(availability.status),
          border: '1px dashed',
          borderColor: getAvailabilityBorderColor(availability.status),
          '&:hover': {
            boxShadow: 3,
            transform: 'translateY(-2px)',
            transition: 'all 0.2s',
            cursor: 'pointer'
          }
        }}
        onDoubleClick={() => handleOpenEditAvailability(availability, staff)}
        onClick={(e) => {
          if (e.ctrlKey || e.metaKey) {
            handleOpenEditAvailability(availability, staff);
          }
        }}
        className="availability-card"
      >
        <CardContent sx={{ p: { xs: 0.5, sm: 1 }, position: 'relative' }}>
          <IconButton
            size="small"
            sx={{
              position: 'absolute',
              top: { xs: 0, sm: 2 },
              right: { xs: 0, sm: 2 },
              color: getAvailabilityBorderColor(availability.status),
              backgroundColor: 'rgba(255,255,255,0.7)',
              padding: { xs: '1px', sm: '2px' },
              '&:hover': {
                backgroundColor: 'rgba(255,255,255,0.9)',
              }
            }}
            onClick={(e) => {
              e.stopPropagation();
              handleOpenEditAvailability(availability, staff);
            }}
            className="no-print"
          >
            <Edit fontSize="small" sx={{ fontSize: { xs: '0.7rem', sm: '0.9rem' } }} />
          </IconButton>
          
          <Typography variant="body2" sx={{ fontSize: { xs: '0.55rem', sm: '0.75rem' }, color: '#333', fontWeight: 'bold', pr: { xs: 2, sm: 3 } }}>
            {availability.startTime} - {availability.endTime}
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 0.5 }}>
            <Chip 
              label="Available"
              size="small"
              sx={{ 
                height: { xs: 14, sm: 16 },
                fontSize: { xs: '0.5rem', sm: '0.6rem' },
                bgcolor: getAvailabilityBorderColor(availability.status),
                color: 'white'
              }}
            />
            <Typography variant="caption" sx={{ fontSize: { xs: '0.5rem', sm: '0.65rem' }, color: '#666' }}>
              {formatHoursMinutes(availability.durationMinutes)}
            </Typography>
          </Box>
        </CardContent>
      </Card>
    </Tooltip>
  );
};

  // Check staff availability for shift
  const checkStaffAvailabilityForShift = useCallback((staffId, shift) => {
    if (!staffId || !shift || !shift.startDateTime) return false;
    
    const staffAvailabilitiesData = staffAvailabilities[staffId] || {};
    
    const staff = staffList.find(s => s._id === staffId);
    const staffJobTitle = staff?.jobtitle;
    const shiftCategory = shift.categoryOfStaff;
    
    // CATEGORY CHECK
    if (shiftCategory && staffJobTitle) {
      const shiftHasHCA = shiftCategory.includes('HCA');
      const staffHasHCA = staffJobTitle.includes('HCA');
      
      if (shiftHasHCA && staffHasHCA) {
        // Both have HCA - allow the match
      } else {
        const categoryMatch = staffJobTitle.includes(shiftCategory) || 
                            shiftCategory.includes(staffJobTitle) ||
                            staffJobTitle === shiftCategory;
        
        if (!categoryMatch) {
          return false;
        }
      }
    } else if (shiftCategory && !staffJobTitle) {
      return false;
    }
    
    const shiftStart = new Date(shift.startDateTime);
    const shiftEnd = new Date(shift.endDateTime);
    //const shiftDate = shiftStart.toISOString().split('T')[0];
    const shiftDate = getLocalDateString(shiftStart.toISOString());

    // Check for sick leave
    const sickLeavesForDate = staffAvailabilitiesData[shiftDate]?.filter(
      a => a.isSickLeave
    ) || [];

    const fullDaySickLeave = sickLeavesForDate.find(
      a => a.sickLeaveType === 'Full Day'
    );
    
    if (fullDaySickLeave) {
      return false;
    }
    
    const partialSickLeaves = sickLeavesForDate.filter(
      a => a.sickLeaveType === 'Partial Day'
    );
    
    for (const sickLeave of partialSickLeaves) {
      const sickStart = new Date(`${shiftDate}T${sickLeave.startTime}`);
      const sickEnd = new Date(`${shiftDate}T${sickLeave.endTime}`);
      
      if (sickLeave.endTime < sickLeave.startTime) {
        sickEnd.setDate(sickEnd.getDate() + 1);
      }
      
      if ((sickStart <= shiftStart && shiftStart < sickEnd) ||
          (sickStart < shiftEnd && shiftEnd <= sickEnd) ||
          (shiftStart <= sickStart && shiftEnd >= sickEnd)) {
        return false;
      }
    }

    const availabilitiesForDate = staffAvailabilitiesData[shiftDate]?.filter(
      a => !a.isUnavailable && !a.isSickLeave
    ) || [];
    
    for (const availability of availabilitiesForDate) {
      const availabilityStart = new Date(`${shiftDate}T${availability.startTime}`);
      const availabilityEnd = new Date(`${shiftDate}T${availability.endTime}`);
      
      if (availability.endTime < availability.startTime) {
        availabilityEnd.setDate(availabilityEnd.getDate() + 1);
      }
      
      if (availabilityStart <= shiftStart && availabilityEnd >= shiftEnd) {
        return true;
      }
      
      if ((availabilityStart <= shiftStart && shiftStart < availabilityEnd) ||
          (availabilityStart < shiftEnd && shiftEnd <= availabilityEnd)) {
        return true;
      }
    }
    
    return false;
  }, [staffAvailabilities, staffList]);

  const checkStaffAvailabilityWithDetails = useCallback((staffId, shift) => {
    if (!staffId || !shift || !shift.startDateTime) {
      return { hasAvailability: false, reason: 'Invalid data' };
    }
    
    const staffAvailabilitiesData = staffAvailabilities[staffId] || {};
    const shiftStart = new Date(shift.startDateTime);
    const shiftEnd = new Date(shift.endDateTime);
    //const shiftDate = shiftStart.toISOString().split('T')[0];
    const shiftDate = getLocalDateString(shiftStart.toISOString());
    
    const staff = staffList.find(s => s._id === staffId);
    const staffJobTitle = staff?.jobtitle;
    const shiftCategory = shift.categoryOfStaff;
    
    // CATEGORY CHECK
    if (shiftCategory && staffJobTitle) {
      const shiftHasHCA = shiftCategory.includes('HCA');
      const staffHasHCA = staffJobTitle.includes('HCA');
      
      if (shiftHasHCA && staffHasHCA) {
        // Both have HCA - allow the match
      } else {
        const categoryMatch = staffJobTitle.includes(shiftCategory) || 
                            shiftCategory.includes(staffJobTitle) ||
                            staffJobTitle === shiftCategory;
        
        if (!categoryMatch) {
          return { 
            hasAvailability: false, 
            reason: `Staff (${staffJobTitle}) does not match required category: ${shiftCategory}`,
            categoryMismatch: true
          };
        }
      }
    } else if (shiftCategory && !staffJobTitle) {
      return { 
        hasAvailability: false, 
        reason: `Shift requires category (${shiftCategory}) but staff has no job title`,
        categoryMismatch: true
      };
    }
    
    // ── Check unavailabilities — only block if times actually overlap ────────
    const unavailabilitiesForDate = staffAvailabilitiesData[shiftDate]?.filter(
      a => a.isUnavailable || a.isSickLeave
    ) || [];

    for (const unavailability of unavailabilitiesForDate) {
      const unavailType = unavailability.unavailabilityType || unavailability.sickLeaveType || 'Full Day';
      if (unavailType === 'Full Day') {
        const reasonText = unavailability.isSickLeave
          ? `Staff is on sick leave: ${unavailability.sicknessReason || 'No reason specified'}`
          : `Staff is unavailable: ${unavailability.unavailabilityReason || 'No reason specified'}`;
        return {
          hasAvailability: false,
          reason: reasonText,
          isUnavailable: true,
          unavailabilityDetails: unavailability
        };
      }
      // Partial — only block if it actually overlaps the shift
      const unavailStart = new Date(`${shiftDate}T${unavailability.startTime}`);
      const unavailEnd = new Date(`${shiftDate}T${unavailability.endTime}`);

      if (unavailability.endTime < unavailability.startTime) {
        unavailEnd.setDate(unavailEnd.getDate() + 1);
      }

      if ((unavailStart <= shiftStart && shiftStart < unavailEnd) ||
          (unavailStart < shiftEnd && shiftEnd <= unavailEnd) ||
          (shiftStart <= unavailStart && shiftEnd >= unavailEnd)) {

        const reasonText = unavailability.isSickLeave
          ? `Shift overlaps with sick leave: ${unavailability.sicknessReason || 'No reason specified'}`
          : `Shift overlaps with unavailability: ${unavailability.unavailabilityReason || 'No reason specified'}`;

        return {
          hasAvailability: false,
          reason: reasonText,
          isUnavailable: true,
          unavailabilityDetails: unavailability
        };
      }
    }

    // Check for availabilities
    const availabilitiesForDate = staffAvailabilitiesData[shiftDate]?.filter(
      a => !a.isUnavailable && !a.isSickLeave
    ) || [];
    
    for (const availability of availabilitiesForDate) {
      const availabilityStart = new Date(`${shiftDate}T${availability.startTime}`);
      const availabilityEnd = new Date(`${shiftDate}T${availability.endTime}`);
      
      if (availability.endTime < availability.startTime) {
        availabilityEnd.setDate(availabilityEnd.getDate() + 1);
      }
      
      if (availabilityStart <= shiftStart && availabilityEnd >= shiftEnd) {
        return { 
          hasAvailability: true, 
          reason: 'Shift fully covered by availability',
          availabilityStatus: availability.status
        };
      }
      
      if ((availabilityStart <= shiftStart && shiftStart < availabilityEnd) ||
          (availabilityStart < shiftEnd && shiftEnd <= availabilityEnd)) {
        return { 
          hasAvailability: true, 
          reason: 'Shift partially covered by availability',
          availabilityStatus: availability.status,
          isPartial: true
        };
      }
    }
    
    const allAvailabilities = Object.values(staffAvailabilitiesData).flat();
    const approvedAvailabilities = allAvailabilities.filter(a => 
      a.status === 'Approved' && !a.isUnavailable && !a.isSickLeave
    );
    
    if (approvedAvailabilities.length === 0) {
      return { 
        hasAvailability: false, 
        reason: 'No approved availabilities',
        totalAvailabilities: allAvailabilities.length
      };
    }
    
    return { 
      hasAvailability: true,
      reason: 'Staff has approved availabilities but not for this specific time',
      isPartial: false
    };
  }, [staffAvailabilities, staffList]);

  const handleDragStart = (e, shift) => {
    setDraggedItem(shift);
    e.dataTransfer.setData('text/plain', JSON.stringify(shift));
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleDrop = async (e, staffId, dayDate) => {
    e.preventDefault();
    
    if (!draggedItem) return;
    
    const availabilityCheck = checkStaffAvailabilityWithDetails(staffId, draggedItem);
    
    if (!availabilityCheck.hasAvailability) {
      const staffName = staffList.find(s => s._id === staffId)?.name || 'Staff member';
      const shiftDate = new Date(draggedItem.startDateTime);
      const shiftTime = `${shiftDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} - ${new Date(draggedItem.endDateTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;

      if (availabilityCheck.isUnavailable) {
        showSnackbar(
          `⛔ ${staffName} is unavailable: ${availabilityCheck.reason} (${shiftTime})`,
          'error'
        );
      } else {
        showSnackbar(
          `❌ ${staffName} cannot be assigned: ${availabilityCheck.reason} (${shiftTime})`,
          'error'
        );
      }
      return;
    }
    
    if (availabilityCheck.isPartial) {
      const staffName = staffList.find(s => s._id === staffId)?.name || 'Staff member';
      showSnackbar(
        `Note: ${staffName}'s availability only partially covers this shift`,
        'warning'
      );
    }
    
    try {
      await assignStaff({
        shiftId: draggedItem._id,
        staffData: {
          userId: staffId,
          userName: staffList.find(s => s._id === staffId)?.name,
          userEmail: staffList.find(s => s._id === staffId)?.email,
          managerId: userInfo._id
        }
      }).unwrap();
      
      const updatedShifts = { ...staffShifts };
      if (!updatedShifts[staffId]) {
        updatedShifts[staffId] = {};
      }
      if (!updatedShifts[staffId][dayDate]) {
        updatedShifts[staffId][dayDate] = [];
      }
      
      const dayOfWeek = new Date(dayDate).getDay();
      const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
      const isAgency = !!draggedItem.filledByAgency;
      
      updatedShifts[staffId][dayDate].push({
        id: draggedItem._id,
        startTime: new Date(draggedItem.startDateTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        endTime: new Date(draggedItem.endDateTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        ward: draggedItem.wardDepartmentArea,
        location: draggedItem.hospitalLocationId?.name,
        categoryOfStaff: draggedItem.categoryOfStaff,
        isWeekendShift: isWeekend,
        isAgencyFilled: isAgency,
        isCancelled: false,
        lineStatus: 'FilledByStaff',
        shiftData: draggedItem
      });
      
      setStaffShifts(updatedShifts);
      setDraggedItem(null);
      
      const updatedAvailable = availableShifts.filter(shift => shift._id !== draggedItem._id);
      setAvailableShifts(updatedAvailable);
      
      const updatedFilteredAvailable = updatedAvailable.filter(shift => 
        selectedWardFilter === 'all' || shift.wardDepartmentArea === selectedWardFilter
      );
      setFilteredAvailableShifts(updatedFilteredAvailable);
      
      refetchShifts();
      refetchAvailable();
      
      showSnackbar('Shift assigned successfully!', 'success');
      
    } catch (error) {
      console.error('Failed to assign shift:', error);
      const errorMessage = error?.data?.message || error?.message || 'Failed to assign shift';
      showSnackbar(errorMessage, 'error');
    }
  };

  const handleShiftDoubleClick = (shift, staffId, dayDate) => {
    if (!shift || !shift.id) return;
    
    const staff = staffList.find(s => s._id === staffId);
    const shiftDate = new Date(shift.shiftData?.startDateTime || dayDate);
    
    setShiftToRelease({
      shiftId: shift.id,
      staffId,
      staffName: staff?.name || 'Unknown Staff',
      shiftDate: shiftDate.toLocaleDateString(),
      shiftTime: `${shift.startTime} - ${shift.endTime}`,
      ward: shift.ward,
      dayDate
    });
    
    setReleaseDialogOpen(true);
  };

  const handleEditShiftClick = (shift, staffId, dayDate) => {
    if (!shift || !shift.shiftData) return;
    
    const shiftData = shift.shiftData;
    const startDateTime = new Date(shiftData.startDateTime);
    const endDateTime = new Date(shiftData.endDateTime);
    
    const startTime = dublinTimeStr(shiftData.startDateTime);
    const endTime = dublinTimeStr(shiftData.endDateTime);
    
    let selectedPreset = '';
    Object.entries(SHIFT_PRESETS).forEach(([key, preset]) => {
      if (preset.start === startTime && preset.end === endTime) {
        selectedPreset = key;
      }
    });
    
    setShiftToEdit({
      shiftId: shift.id,
      staffId,
      staffName: staffList.find(s => s._id === staffId)?.name || 'Unknown',
      dayDate
    });
    
    setEditForm({
      //startDate: startDateTime.toISOString().split('T')[0],
      startDate: getLocalDateString(startDateTime.toISOString()),
      startTime: startTime,
      //endDate: endDateTime.toISOString().split('T')[0],
      endDate:   getLocalDateString(endDateTime.toISOString()),
      endTime: endTime,
      selectedPreset: selectedPreset,
      hospitalLocationId: shiftData.hospitalLocationId?._id || shiftData.hospitalLocationId || '',
      wardDepartmentArea: shiftData.wardDepartmentArea || '',
      categoryOfStaff: shiftData.categoryOfStaff || '',
      reasonForRequest: shiftData.reasonForRequest || '',
      reasonForRequestDetails: shiftData.reasonForRequestDetails || '',
      escort: shiftData.escort || false,
      special: shiftData.special || false,
      isOvertime: shiftData.isOvertime || false,
      isActive: shiftData.isActive !== false,
      eccNumber: shiftData.eccNumber || '',
      specialRequirements: shiftData.specialRequirements || '',
      comments: shiftData.comments || '',
      specialsReason: shiftData.specialsReason || '',   
    });
    
    setActiveTab('basic');
    setEditDialogOpen(true);
  };

  const handleReleaseShift = async () => {
    if (!shiftToRelease) return;
    
    try {
      await reOpenShift({
        shiftId: shiftToRelease.shiftId,
        userId: shiftToRelease.staffId,
        userName: shiftToRelease.staffName,
        userEmail: '',
        managerId: userInfo._id
      }).unwrap();
      
      const updatedShifts = { ...staffShifts };
      if (updatedShifts[shiftToRelease.staffId] && updatedShifts[shiftToRelease.staffId][shiftToRelease.dayDate]) {
        updatedShifts[shiftToRelease.staffId][shiftToRelease.dayDate] = 
          updatedShifts[shiftToRelease.staffId][shiftToRelease.dayDate].filter(s => s.id !== shiftToRelease.shiftId);
        setStaffShifts(updatedShifts);
      }
      
      const shiftDate = new Date(shiftToRelease.dayDate);
      const weekStart = new Date(weekInfo.startDate);
      const weekEnd = new Date(weekInfo.endDate);
      
      if (shiftDate >= weekStart && shiftDate <= weekEnd) {
        refetchAvailable();
      }
      
      refetchShifts();
      
      showSnackbar('Shift released successfully!', 'success');
      
    } catch (error) {
      console.error('Failed to release shift:', error);
      showSnackbar('Failed to release shift', 'error');
    } finally {
      setReleaseDialogOpen(false);
      setShiftToRelease(null);
    }
  };

  const handleUpdateShift = async () => {
    if (!shiftToEdit || !shiftToEdit.shiftId) return;
    
    try {

      const isOvernight = editForm.endTime < editForm.startTime && editForm.startDate === editForm.endDate;
      const addOneDay = (dateStr) => {
        const [y,m,d] = dateStr.split('-').map(Number);
        const dt = new Date(Date.UTC(y,m-1,d));
        dt.setUTCDate(dt.getUTCDate()+1);
        return dt.toISOString().split('T')[0];
      };
      const resolvedEndDate = isOvernight ? addOneDay(editForm.endDate) : editForm.endDate;

      
      const formattedData = {
        shiftId: shiftToEdit.shiftId,
        startDateTime: dublinLocalToUtcISO(editForm.startDate, editForm.startTime),
        endDateTime: dublinLocalToUtcISO(resolvedEndDate, editForm.endTime),
        hospitalLocationId: editForm.hospitalLocationId,
        wardDepartmentArea: editForm.wardDepartmentArea,
        reasonForRequest: editForm.reasonForRequest,
        reasonForRequestDetails: editForm.reasonForRequestDetails,
        categoryOfStaff: editForm.categoryOfStaff,
        escort: editForm.escort,
        special: editForm.special,
        isOvertime: editForm.isOvertime,
        isActive: editForm.isActive,
        eccNumber: editForm.eccNumber,
        specialRequirements: editForm.specialRequirements,
        comments: editForm.comments,
        specialsReason: editForm.specialsReason || '',  
      };
      
      await updateShift(formattedData).unwrap();
      
      const updatedShifts = { ...staffShifts };
      if (updatedShifts[shiftToEdit.staffId] && updatedShifts[shiftToEdit.staffId][shiftToEdit.dayDate]) {
        const shiftIndex = updatedShifts[shiftToEdit.staffId][shiftToEdit.dayDate].findIndex(
          s => s.id === shiftToEdit.shiftId
        );
        
        if (shiftIndex !== -1) {
          const updatedShift = {
            ...updatedShifts[shiftToEdit.staffId][shiftToEdit.dayDate][shiftIndex],
            startTime: new Date(formattedData.startDateTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
            endTime: new Date(formattedData.endDateTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
            ward: editForm.wardDepartmentArea,
            categoryOfStaff: editForm.categoryOfStaff,
          };
          
          updatedShifts[shiftToEdit.staffId][shiftToEdit.dayDate][shiftIndex] = updatedShift;
          setStaffShifts(updatedShifts);
        }
      }
      
      refetchShifts();
      
      showSnackbar('Shift updated successfully!', 'success');
      setEditDialogOpen(false);
      setShiftToEdit(null);
      
    } catch (error) {
      console.error('Failed to update shift:', error);
      //showSnackbar('Failed to update shift', 'error');
      showSnackbar(error?.data?.message || 'Failed to create shift', 'error');
    }
  };

  const handleCancelRelease = () => {
    setReleaseDialogOpen(false);
    setShiftToRelease(null);
  };

  const handleCancelEdit = () => {
    setEditDialogOpen(false);
    setShiftToEdit(null);
  };

  const handleCancelEmail = () => {
    setEmailDialogOpen(false);
    setSelectedStaffForEmail(null);
  };

  const handleEditFormChange = (e) => {
  const { name, value, type, checked } = e.target;
  setEditForm(prev => {
    const next = { ...prev, [name]: type === 'checkbox' ? checked : value };
    if (name === 'reasonForRequest' &&
        !['1-1 special', 'Cohorts'].some(r => value?.toLowerCase() === r.toLowerCase())) {
      next.specialsReason = '';
    }
    return next;
  });
};

  const handleNavigateToCalendarView = () => {
    navigate('/admin/MonthlyCalendarView');
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({
      open: true,
      message,
      severity
    });
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const navigateWeek = (direction) => {
    const newDate = new Date(selectedWeek);
    newDate.setDate(newDate.getDate() + (direction * 7));
    setSelectedWeek(newDate);
  };

  const handleDateChange = (event) => {
    const newDate = new Date(event.target.value);
    if (!isNaN(newDate.getTime())) {
      setSelectedWeek(newDate);
    }
  };

  const goToCurrentWeek = () => {
    setSelectedWeek(new Date());
  };

  const formatDateRange = () => {
    const start = new Date(weekInfo.startDate);
    const end = new Date(weekInfo.endDate);
    return `${start.toLocaleDateString()} - ${end.toLocaleDateString()}`;
  };

  const handleClearFilters = () => {
    setSelectedWardFilter('all');
    setSelectedJobTitleFilter('all');
    setSelectedStaffFilter('all');
    setSelectedDayFilter('all');
    showSnackbar('Filters cleared', 'info');
  };

  const getFilterSummary = () => {
    const filters = [];
    if (selectedWardFilter !== 'all') filters.push(`Ward: ${selectedWardFilter}`);
    if (selectedJobTitleFilter !== 'all') filters.push(`Job: ${selectedJobTitleFilter}`);
    if (selectedDayFilter !== 'all') {
      const day = weekInfo.days.find(d => d.date === selectedDayFilter);
      filters.push(`Availability: ${day ? day.dayName : selectedDayFilter}`);
    }
    if (isManager && !isAdmin && managerDirectorate) filters.push(`Directorate: ${managerDirectorate}`);
    return filters.length > 0 ? filters.join(' • ') : 'No filters applied';
  };

  // Print function
  const handlePrint = () => {
    debugAvailabilityData();
    
    const printWindow = window.open('', '_blank');
    
    if (!printWindow) {
      showSnackbar('Please allow pop-ups to print', 'warning');
      return;
    }
    
    const filterInfo = getFilterSummary();
    
    const isAvailabilityUnavailable = (availability) => {
      return availability.isUnavailable || availability.isSickLeave || availability.isActuallyUnavailable;
    };
    
    const printHTML = `
      <!DOCTYPE html>
      <html lang="en">
      <head>
        <meta charset="UTF-8">
        <meta name="viewport" content="width=device-width, initial-scale=1.0">
        <title>Staff Weekly Schedule - ${formatDateRange()}</title>
        <style>
          @page {
            size: landscape;
            margin: 0.5in;
          }
          
          body {
            font-family: Arial, sans-serif;
            margin: 0;
            padding: 20px;
            background: white;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
            color-adjust: exact !important;
            font-size: 10px;
          }
          
          .print-header {
            text-align: center;
            margin-bottom: 20px;
            border-bottom: 2px solid #000;
            padding-bottom: 10px;
          }
          
          .print-title {
            font-size: 24px;
            font-weight: bold;
            margin-bottom: 5px;
          }
          
          .print-subtitle {
            font-size: 16px;
            color: #666;
            margin-bottom: 10px;
          }
          
          .print-info {
            font-size: 12px;
            color: #666;
            margin-bottom: 5px;
          }
          
          .print-calendar {
            width: 100%;
            border-collapse: collapse;
            table-layout: fixed;
            margin-top: 20px;
          }
          
          .print-calendar th {
            background-color: #1976d2;
            color: white;
            padding: 10px;
            text-align: center;
            border: 1px solid #ddd;
            font-weight: bold;
          }
          
          .print-calendar td {
            padding: 5px;
            border: 1px solid #ddd;
            vertical-align: top;
            min-height: 150px;
            page-break-inside: avoid;
            break-inside: avoid;
          }
          
          .staff-header {
            background-color: #1976d2;
            color: white;
            padding: 8px;
            text-align: left;
            font-weight: bold;
            width: 200px;
          }
          
          .day-header {
            background-color: #1976d2;
            color: white;
            padding: 8px;
            text-align: center;
            font-weight: bold;
          }
          
          .weekend-header {
            background-color: #ff9800 !important;
          }
          
          .unavailable-card {
            padding: 6px;
            margin: 3px 0;
            border-radius: 6px;
            font-size: 9px;
            color: white !important;
            font-weight: bold;
            page-break-inside: avoid;
            break-inside: avoid;
            min-height: 50px;
            border: 3px solid rgba(0,0,0,0.3) !important;
            box-shadow: 0 2px 4px rgba(0,0,0,0.2);
          }
          
          .unavailable-sick-full {
            background-color: #f44336 !important;
            border-color: #d32f2f !important;
          }
          
          .unavailable-sick-partial {
            background-color: #ff9800 !important;
            border-color: #e65100 !important;
          }
          
          .unavailable-other {
            background-color: #d32f2f !important;
            border-color: #b71c1c !important;
          }
          
          .unavailable-force-majeure {
            background-color: #9c27b0 !important;
            border-color: #7b1fa2 !important;
          }
          
          .availability-card {
            padding: 4px;
            margin: 2px 0;
            border-radius: 4px;
            font-size: 9px;
            color: white !important;
            font-weight: bold;
            page-break-inside: avoid;
            break-inside: avoid;
            min-height: 40px;
            border: 1px dashed rgba(255,255,255,0.5);
          }
          
          .availability-pending {
            background-color: #2196f3 !important;
          }
          
          .availability-approved {
            background-color: #4caf50 !important;
          }
          
          .availability-rejected {
            background-color: #f44336 !important;
          }
          
          .shift-card {
            padding: 4px;
            margin: 2px 0;
            border-radius: 4px;
            font-size: 9px;
            color: white !important;
            font-weight: bold;
            page-break-inside: avoid;
            break-inside: avoid;
            min-height: 40px;
            border: 1px solid rgba(0,0,0,0.1);
          }
          
          .shift-assigned {
            background-color: #4caf50 !important;
          }
          
          .shift-weekend-assigned {
            background-color: #ff9800 !important;
          }
          
          .shift-agency {
            background-color: #9c27b0 !important;
          }
          
          .shift-cancelled {
            background-color: #f44336 !important;
          }
          
          .shift-time {
            font-weight: bold;
            font-size: 8px;
            margin-bottom: 2px;
          }
          
          .unavailable-label {
            font-size: 8px;
            font-weight: bold;
            margin-bottom: 2px;
            text-transform: uppercase;
          }
          
          .unavailable-reason {
            font-size: 7px;
            margin-top: 2px;
            font-style: italic;
            opacity: 0.9;
          }
          
          .availability-label {
            font-size: 8px;
            margin-bottom: 2px;
          }
          
          .availability-details {
            font-size: 7px;
            margin-top: 2px;
            opacity: 0.9;
          }
          
          .staff-info {
            padding: 8px;
            background-color: #f5f5f5;
            border-right: 1px solid #ddd;
            min-width: 180px;
          }
          
          .staff-name {
            font-weight: bold;
            font-size: 11px;
          }
          
          .staff-details {
            font-size: 9px;
            color: #666;
            margin-top: 2px;
          }
          
          .staff-job {
            font-size: 9px;
            color: #1976d2;
            font-weight: bold;
          }
          
          .empty-day {
            text-align: center;
            color: #999;
            font-style: italic;
            font-size: 9px;
            padding: 20px 5px;
          }
          
          @media print {
            * {
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
              color-adjust: exact !important;
            }
          }
        </style>
      </head>
      <body>
        <div class="print-header">
          <div class="print-title">Staff Weekly Schedule</div>
          <div class="print-subtitle">Week of ${formatDateRange()}</div>
          <div class="print-info">Generated: ${new Date().toLocaleDateString()} ${new Date().toLocaleTimeString()}</div>
          <div class="print-info">Generated By: ${userInfo?.name || 'Manager'}</div>
          ${filterInfo !== 'No filters applied' ? `<div class="print-info">Filters: ${filterInfo}</div>` : ''}
          <div class="print-info">Showing ${filteredStaffList.length} staff members</div>
        </div>
        
        <table class="print-calendar">
          <thead>
            <tr>
              <th class="staff-header" style="width: 180px;">Staff Information</th>
              ${weekInfo.days.map(day => `
                <th class="day-header ${day.isWeekend ? 'weekend-header' : ''}" style="width: calc((100% - 180px) / 7);">
                  ${day.dayName}<br>
                  <span style="font-size: 11px; font-weight: normal;">${day.date}</span>
                  ${day.isWeekend ? '<div style="font-size: 10px; font-style: italic;">Weekend</div>' : ''}
                </th>
              `).join('')}
            </tr>
          </thead>
          <tbody>
            ${filteredStaffList.map((staff, index) => {
              const staffShiftsData = staffShifts[staff._id] || {};
              const staffAvailabilitiesData = staffAvailabilities[staff._id] || {};
              
              return `
                <tr>
                  <td class="staff-info" style="background-color: ${index % 2 === 0 ? '#f9f9f9' : 'white'};">
                    <div class="staff-name">${staff.name}</div>
                    <div class="staff-job">${staff.jobtitle || 'No title'}</div>
                    <div class="staff-details">${staff.email || ''}</div>
                  </td>
                  ${weekInfo.days.map(day => {
                    const shifts = staffShiftsData[day.date] || [];
                    const availabilities = staffAvailabilitiesData[day.date] || [];
                    
                    return `
                      <td style="background-color: ${day.isWeekend ? 'rgba(255, 152, 0, 0.05)' : (index % 2 === 0 ? '#f9f9f9' : 'white')};">
                        ${shifts.map(shift => {
                          let shiftClass = 'shift-card ';
                          if (shift.isCancelled) shiftClass += 'shift-cancelled';
                          else if (shift.isAgencyFilled) shiftClass += 'shift-agency';
                          else if (shift.isWeekendShift) shiftClass += 'shift-weekend-assigned';
                          else shiftClass += 'shift-assigned';
                          
                          return `
                            <div class="${shiftClass}">
                              <div class="shift-time">${shift.startTime} - ${shift.endTime}</div>
                              <div class="availability-label">${shift.ward}</div>
                              <div class="availability-details">${shift.categoryOfStaff}</div>
                            </div>
                          `;
                        }).join('')}
                        
                         ${shifts.length === 0 ? availabilities.map((availability, idx) => {
                          const isUnavailableRecord = isAvailabilityUnavailable(availability);
                          
                          if (isUnavailableRecord) {
                            let unavailableClass = 'unavailable-card ';
                            const reason = availability.unavailabilityReason || availability.sicknessReason || 'Unavailable';
                            
                            if (availability.isSickLeave) {
                              const isPartial = availability.sickLeaveType === 'Partial Day' || 
                                               availability.sickLeaveType === 'Half Day AM' || 
                                               availability.sickLeaveType === 'Half Day PM';
                              unavailableClass += isPartial ? 'unavailable-sick-partial' : 'unavailable-sick-full';
                            } else if (reason.toLowerCase().includes('force majeure')) {
                              unavailableClass += 'unavailable-force-majeure';
                            } else {
                              unavailableClass += 'unavailable-other';
                            }
                            
                            const type = availability.unavailabilityType || availability.sickLeaveType || 'Full Day';
                            
                            return `
                              <div class="${unavailableClass}">
                                <div class="shift-time">${availability.startTime} - ${availability.endTime}</div>
                                <div class="unavailable-label">⛔ UNAVAILABLE</div>
                                <div class="unavailable-reason">${type}</div>
                                <div class="unavailable-reason">${reason}</div>
                                ${availability.comments ? `<div class="unavailable-reason">${availability.comments}</div>` : ''}
                              </div>
                            `;
                          } else {
                            let availabilityClass = 'availability-card ';
                            if (availability.status === 'Approved') availabilityClass += 'availability-approved';
                            else if (availability.status === 'Rejected') availabilityClass += 'availability-rejected';
                            else availabilityClass += 'availability-pending';
                            
                            return `
                              <div class="${availabilityClass}">
                                <div class="shift-time">${availability.startTime} - ${availability.endTime}</div>
                                <div class="availability-label">Available</div>
                                <div class="availability-details">Available</div>
                                <div class="availability-details">Duration: ${formatHoursMinutes(availability.durationMinutes)}</div>
                              </div>
                            `;
                          }
                        }).join('') : ''}
                        
                        ${shifts.length === 0 && availabilities.length === 0 ? `
                          <div class="empty-day">
                            No shifts or availability
                          </div>
                        ` : ''}
                      </td>
                    `;
                  }).join('')}
                </tr>
              `;
            }).join('')}
          </tbody>
        </table>
        
        <div style="margin-top: 30px; padding: 15px; border-top: 2px solid #000; font-size: 10px; color: #666; text-align: center;">
          Beaumont Hospital Staff Scheduling System<br>
          Generated on ${new Date().toLocaleDateString()} at ${new Date().toLocaleTimeString()}<br>
          For questions, contact: beaumontNursingBank@beaumont.ie
        </div>
        
        <script>
          window.onload = function() {
            setTimeout(() => {
              window.print();
              setTimeout(() => {
                window.close();
              }, 100);
            }, 500);
          };
        </script>
      </body>
      </html>
    `;
    
    printWindow.document.write(printHTML);
    printWindow.document.close();
  };


  const handlePrintShort = () => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) { showSnackbar('Please allow pop-ups to print', 'warning'); return; }
    const filterInfo = getFilterSummary();

    const printHTML = `
      <!DOCTYPE html><html lang="en"><head><meta charset="UTF-8">
      <title>Staff Weekly Schedule (Short) - ${formatDateRange()}</title>
      <style>
        @page { size: landscape; margin: 0.4in; }
        body { font-family: Arial, sans-serif; margin: 0; padding: 10px; background: white;
          -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; font-size: 9px; }
        .print-header { text-align: center; margin-bottom: 10px; border-bottom: 2px solid #000; padding-bottom: 8px; }
        .print-title { font-size: 18px; font-weight: bold; margin-bottom: 3px; }
        .print-subtitle { font-size: 12px; color: #666; }
        .print-info { font-size: 10px; color: #666; }
        table { width: 100%; border-collapse: collapse; table-layout: fixed; margin-top: 10px; }
        th { background-color: #1976d2; color: white; padding: 6px 4px; text-align: center; border: 1px solid #ccc; font-size: 9px; }
        th.weekend-header { background-color: #ff9800; }
        td { padding: 3px; border: 1px solid #ddd; vertical-align: top; }
        .staff-cell { background-color: #f5f5f5; width: 140px; }
        .staff-name { font-weight: bold; font-size: 9px; }
        .staff-job { font-size: 8px; color: #1976d2; font-weight: bold; }
        .shift-card { padding: 3px 4px; margin: 2px 0; border-radius: 3px; font-size: 8px; font-weight: bold; color: white !important; }
        .shift-assigned { background-color: #4caf50 !important; }
        .shift-weekend-assigned { background-color: #ff9800 !important; }
        .shift-agency { background-color: #9c27b0 !important; }
        .shift-cancelled { background-color: #f44336 !important; }
        .avail-card { padding: 3px 4px; margin: 2px 0; border-radius: 3px; font-size: 8px; font-weight: bold; color: white !important; }
        .avail-pending { background-color: #2196f3 !important; }
        .avail-approved { background-color: #4caf50 !important; }
        .avail-rejected { background-color: #f44336 !important; }
        .unavail-card { padding: 3px 4px; margin: 2px 0; border-radius: 3px; font-size: 8px; font-weight: bold; color: white !important; border: 2px solid rgba(0,0,0,0.2); }
        .unavail-sick-full { background-color: #f44336 !important; }
        .unavail-sick-partial { background-color: #ff9800 !important; }
        .unavail-other { background-color: #d32f2f !important; }
        .empty-day { text-align: center; color: #bbb; font-style: italic; font-size: 8px; padding: 6px 2px; }
        @media print { * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; } }
      </style></head><body>
      <div class="print-header">
        <div class="print-title">Staff Weekly Schedule</div>
        <div class="print-subtitle">Week of ${formatDateRange()}</div>
        <div class="print-info">
          Generated: ${new Date().toLocaleDateString()} ${new Date().toLocaleTimeString()} &nbsp;|&nbsp;
          By: ${userInfo?.name || 'Manager'} &nbsp;|&nbsp; ${filteredStaffList.length} staff
          ${filterInfo !== 'No filters applied' ? ` &nbsp;|&nbsp; Filters: ${filterInfo}` : ''}
        </div>
      </div>
      <table>
        <thead><tr>
          <th style="width:140px;">Staff</th>
          ${weekInfo.days.map(day => `
            <th class="${day.isWeekend ? 'weekend-header' : ''}">
              ${day.shortName}<br>
              <span style="font-weight:normal;">${new Date(day.date).getDate()}/${new Date(day.date).getMonth() + 1}</span>
            </th>`).join('')}
        </tr></thead>
        <tbody>
          ${filteredStaffList.map((staff, index) => {
            const staffShiftsData       = staffShifts[staff._id]         || {};
            const staffAvailabilitiesData = staffAvailabilities[staff._id] || {};
            return `<tr>
              <td class="staff-cell" style="background-color:${index % 2 === 0 ? '#f9f9f9' : 'white'};">
                <div class="staff-name">${staff.name}</div>
                <div class="staff-job">${staff.jobtitle || 'No title'}</div>
              </td>
              ${weekInfo.days.map(day => {
                const shifts         = staffShiftsData[day.date]           || [];
                const availabilities = staffAvailabilitiesData[day.date]   || [];
                return `<td style="background-color:${day.isWeekend ? 'rgba(255,152,0,0.05)' : (index % 2 === 0 ? '#f9f9f9' : 'white')};">
                  ${shifts.map(shift => {
                    let cls = 'shift-card ';
                    if (shift.isCancelled)      cls += 'shift-cancelled';
                    else if (shift.isAgencyFilled)  cls += 'shift-agency';
                    else if (shift.isWeekendShift)  cls += 'shift-weekend-assigned';
                    else                            cls += 'shift-assigned';
                    return `<div class="${cls}"><div>${shift.startTime} - ${shift.endTime}</div><div>${shift.ward}</div></div>`;
                  }).join('')}
                  ${shifts.length === 0 ? availabilities.map(availability => {
                    const isUnavailableRecord =
                      availability.isUnavailable || availability.isSickLeave || availability.isActuallyUnavailable;
                    if (isUnavailableRecord) {
                      const isSick = availability.isSickLeave;
                      const isPartial =
                        ['Partial Day','Half Day AM','Half Day PM'].includes(availability.sickLeaveType) ||
                        ['Partial Day','Half Day AM','Half Day PM'].includes(availability.unavailabilityType);
                      const cls = 'unavail-card ' + (isSick ? (isPartial ? 'unavail-sick-partial' : 'unavail-sick-full') : 'unavail-other');
                      const reason = availability.unavailabilityReason || availability.sicknessReason || 'Unavailable';
                      return `<div class="${cls}"><div>${isSick ? '🏥 SICK' : '⛔ UNAVAIL'}</div><div>${reason}</div></div>`;
                    }
                    const cls = 'avail-card ' + (
                      availability.status === 'Approved' ? 'avail-approved' :
                      availability.status === 'Rejected' ? 'avail-rejected' : 'avail-pending'
                    );
                    return `<div class="${cls}"><div>${availability.startTime} - ${availability.endTime}</div><div>Available</div></div>`;
                  }).join('') : ''}
                  ${shifts.length === 0 && availabilities.length === 0 ? `<div class="empty-day">—</div>` : ''}
                </td>`;
              }).join('')}
            </tr>`;
          }).join('')}
        </tbody>
      </table>
      <div style="margin-top:20px;padding-top:10px;border-top:1px solid #ccc;font-size:9px;color:#888;text-align:center;">
        Beaumont Hospital Staff Scheduling System &nbsp;|&nbsp;
        ${new Date().toLocaleDateString()} ${new Date().toLocaleTimeString()}
      </div>
      <script>window.onload=function(){setTimeout(()=>{window.print();setTimeout(()=>window.close(),100);},500);};</script>
    </body></html>`;

    printWindow.document.write(printHTML);
    printWindow.document.close();
  };

  // Print available shifts (from today onwards, respecting current filters)
  const handlePrintAvailableShifts = () => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) { showSnackbar('Please allow pop-ups to print', 'warning'); return; }

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const shiftsToPrint = [...filteredAvailableShifts]
      .filter(shift => new Date(shift.startDateTime) >= today)
      .sort((a, b) => {
        const dA = new Date(a.startDateTime), dB = new Date(b.startDateTime);
        if (dA - dB !== 0) return dA - dB;
        return (a.wardDepartmentArea || '').localeCompare(b.wardDepartmentArea || '');
      });

    const printHTML = `
      <!DOCTYPE html><html lang="en"><head><meta charset="UTF-8">
      <title>Available Shifts - From ${today.toLocaleDateString()}</title>
      <style>
        @page { size: landscape; margin: 0.4in; }
        body { font-family: Arial, sans-serif; margin: 0; padding: 10px; background: white;
          -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; font-size: 9px; }
        .print-header { text-align: center; margin-bottom: 12px; border-bottom: 2px solid #000; padding-bottom: 8px; }
        .print-title { font-size: 18px; font-weight: bold; margin-bottom: 3px; }
        .print-info { font-size: 10px; color: #666; }
        table { width: 100%; border-collapse: collapse; table-layout: fixed; margin-top: 10px; }
        th { background-color: #1976d2; color: white; padding: 6px 4px; text-align: center; border: 1px solid #ccc; font-size: 9px; }
        td { padding: 4px 6px; border: 1px solid #ddd; vertical-align: middle; font-size: 9px; }
        tr:nth-child(even) { background-color: #f9f9f9; }
        .weekend-row { background-color: #fff8e1 !important; }
        .badge { display: inline-block; padding: 2px 5px; border-radius: 3px; font-size: 8px; font-weight: bold; color: white; }
        .badge-weekend { background-color: #ff9800; }
        .badge-weekday { background-color: #2196f3; }
        .footer { margin-top: 16px; padding-top: 8px; border-top: 1px solid #ccc; font-size: 9px; color: #888; text-align: center; }
        @media print { * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; } }
      </style></head><body>
      <div class="print-header">
        <div class="print-title">Available Shifts</div>
        <div class="print-info">
          From: ${today.toLocaleDateString()}
          &nbsp;|&nbsp; Generated: ${new Date().toLocaleDateString()} ${new Date().toLocaleTimeString()}
          &nbsp;|&nbsp; By: ${userInfo?.name || 'Manager'}
          &nbsp;|&nbsp; ${shiftsToPrint.length} shift${shiftsToPrint.length !== 1 ? 's' : ''}
          ${isManager && !isAdmin && managerDirectorate ? ` &nbsp;|&nbsp; Directorate: ${managerDirectorate}` : ''}
          ${selectedStaffFilter !== 'all' ? ` &nbsp;|&nbsp; Staff filter: ${staffList.find(s => s._id === selectedStaffFilter)?.name || ''}` : ''}
          ${selectedWardFilter !== 'all' ? ` &nbsp;|&nbsp; Ward filter: ${selectedWardFilter}` : ''}
        </div>
      </div>
      ${shiftsToPrint.length === 0 ? `<p style="text-align:center;color:#666;margin-top:40px;">No available shifts from today onwards.</p>` : `
      <table>
        <thead><tr>
          <th style="width:10%">Date</th><th style="width:7%">Day</th><th style="width:11%">Start</th>
          <th style="width:11%">End</th><th style="width:22%">Ward</th><th style="width:18%">Category</th>
          <th style="width:14%">Location</th><th style="width:7%">Type</th>
        </tr></thead>
        <tbody>
          ${shiftsToPrint.map(shift => {
            const sd = new Date(shift.startDateTime);
            const ed = new Date(shift.endDateTime);
            const dow = sd.getDay();
            const isWeekend = dow === 0 || dow === 6;
            const dayNames = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];
            return `<tr class="${isWeekend ? 'weekend-row' : ''}">
              <td>${sd.toLocaleDateString()}</td>
              <td>${dayNames[dow]}</td>
              <td>${sd.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</td>
              <td>${ed.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</td>
              <td>${shift.wardDepartmentArea || '—'}</td>
              <td>${shift.categoryOfStaff || '—'}</td>
              <td>${shift.hospitalLocationId?.name || '—'}</td>
              <td><span class="badge ${isWeekend ? 'badge-weekend' : 'badge-weekday'}">${isWeekend ? 'Weekend' : 'Weekday'}</span></td>
            </tr>`;
          }).join('')}
        </tbody>
      </table>`}
      <div class="footer">Beaumont Hospital Staff Scheduling System &nbsp;|&nbsp; ${new Date().toLocaleDateString()} ${new Date().toLocaleTimeString()}</div>
      <script>window.onload=function(){setTimeout(()=>{window.print();setTimeout(()=>window.close(),100);},500);};</script>
    </body></html>`;

    printWindow.document.write(printHTML);
    printWindow.document.close();
  };

  
  // Email functions
  const handleEmailButtonClick = (staffId) => {
    const staff = staffList.find(s => s._id === staffId);
    if (!staff) return;
    
    setSelectedStaffForEmail({
      id: staffId,
      name: staff.name,
      email: staff.email
    });
    
    const defaultSubject = `Your Weekly Schedule - Week of ${formatDateRange()}`;
    const plainTextBody = generateSchedulePlainText(staffId);
    
    setEmailSubject(defaultSubject);
    setEmailContent(plainTextBody);
    
    if (emailMethod === 'outlook' && staff.email) {
      try {
        openOutlookEmail(staffId, staff.email, defaultSubject, plainTextBody);
        
        showSnackbar('Opening Outlook...', 'info');
        
        return;
      } catch (error) {
        console.error('Failed to open Outlook:', error);
        showSnackbar('Could not open Outlook. Using web email.', 'warning');
        setEmailMethod('web');
      }
    }
    
    setEmailDialogOpen(true);
  };

  const handleSendEmail = () => {
    if (emailMethod === 'outlook') {
      setEmailDialogOpen(false);
    } else {
      handleSendWebEmail();
    }
  };

  if (loadingStaff || loadingShifts || loadingLocations) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <PrintStyles />
      
      {/* Navigation Breadcrumbs */}
      <Breadcrumbs 
        aria-label="breadcrumb" 
        separator={<NavigateNext fontSize="small" />}
        sx={{ mb: 2 }}
      >
        <Link
          underline="hover"
          color="inherit"
          href="#"
          onClick={(e) => {
            e.preventDefault();
            navigate('/dashboard');
          }}
        >
          Dashboard
        </Link>
        <Typography color="text.primary">Staff Weekly View</Typography>
      </Breadcrumbs>
      
      {/* Header with title and buttons */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" gutterBottom sx={{ mb: 0 }}>
            Staff Weekly Schedule
          </Typography>
          {isManager && managerDirectorate && (
            <Typography variant="subtitle1" color="primary" sx={{ mt: 1 }}>
              Directorate: {managerDirectorate}
              {inclusionIds.size > 0 && (
                <Typography component="span" variant="body2" color="success.main" sx={{ ml: 1.5, fontWeight: 600 }}>
                  + {inclusionIds.size} additional ward{inclusionIds.size > 1 ? 's' : ''} included
                </Typography>
              )}
            </Typography>
          )}
        </Box>
        
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="contained"
            startIcon={<Print />}
            onClick={handlePrint}
            sx={{
              bgcolor: '#673ab7',
              '&:hover': {
                bgcolor: '#5e35b1'
              }
            }}
            className="no-print"
          >
            Print Schedule
          </Button>

          <Button
            variant="contained"
            startIcon={<Print />}
            onClick={handlePrintShort}
            sx={{
              bgcolor: '#2e7d32',
              '&:hover': {
                bgcolor: '#1b5e20'
              }
            }}
            className="no-print"
          >
            Print Short Schedule
          </Button>

          <Button
            variant="contained"
            startIcon={<Print />}
            onClick={handlePrintAvailableShifts}
            sx={{
              bgcolor: '#0288d1',
              '&:hover': {
                bgcolor: '#01579b'
              }
            }}
            className="no-print"
          >
            Print Available Shifts
          </Button>
          
          <Button
            variant="contained"
            startIcon={<CalendarMonth />}
            onClick={handleNavigateToCalendarView}
            sx={{
              bgcolor: '#1976d2',
              '&:hover': {
                bgcolor: '#1565c0'
              }
            }}
            className="no-print"
          >
            View Calendar Analytics
          </Button>
        </Box>
      </Box>
      
      {/* Top Row: Week Navigation, Date Picker, and Filters */}
      <Grid container spacing={2} sx={{ mb: 3 }} className="no-print">
        {/* Current Week Selector and Date Picker */}
        <Grid item xs={6}>
          <Paper elevation={1} sx={{ p: 2, height: '100%', bgcolor: '#f5f5f5' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <IconButton onClick={() => navigateWeek(-1)}>
                  <ArrowBackIos />
                </IconButton>
                <Typography variant="h6">
                  Week of {formatDateRange()}
                </Typography>
                <IconButton onClick={() => navigateWeek(1)}>
                  <ArrowForwardIos />
                </IconButton>
              </Box>
            </Box>
            
            <Button 
              startIcon={<Today />} 
              onClick={goToCurrentWeek}
              variant="outlined"
              fullWidth
              sx={{ mb: 2 }}
            >
              Current Week
            </Button>
            
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <CalendarMonth color="action" />
              <TextField
                type="date"
                value={selectedWeek.toISOString().split('T')[0]}
                onChange={handleDateChange}
                size="small"
                label="Select Date"
                InputLabelProps={{ shrink: true }}
                sx={{ width: '100%' }}
              />
            </Box>
            <Tooltip title="Select any date to view its week">
              <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mb: 1 }}>
                (shows week containing selected date)
              </Typography>
            </Tooltip>
            
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 2 }}>
              <Chip 
                label={`${filteredStaffList.length} Staff`}
                color="primary"
                variant="outlined"
                size="small"
              />
              <Chip 
                label={`${filteredAvailableShifts.length} Available Shifts`}
                color="secondary"
                variant="outlined"
                size="small"
              />
            </Box>
          </Paper>
        </Grid>
        
        {/* Filters */}
        <Grid item xs={6}>
          <Paper elevation={1} sx={{ p: 2, height: '100%', bgcolor: '#f5f5f5' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <FilterAlt color="primary" />
                <Typography variant="subtitle1" fontWeight="bold">
                  Filters
                </Typography>
              </Box>
              
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  size="small"
                  onClick={handleClearFilters}
                  disabled={selectedWardFilter === 'all' && selectedJobTitleFilter === 'all' && selectedStaffFilter === 'all' && selectedDayFilter === 'all'}
                >
                  Clear All
                </Button>
              </Box>
            </Box>

            <FormControl fullWidth size="small" sx={{ mb: 1.5 }}>
              <InputLabel>Filter by Job Title</InputLabel>
              <Select
                value={selectedJobTitleFilter}
                label="Filter by Job Title"
                onChange={(e) => setSelectedJobTitleFilter(e.target.value)}
                startAdornment={<FilterList fontSize="small" sx={{ mr: 1 }} />}
              >
                <MenuItem value="all">
                  <em>All Job Titles</em>
                </MenuItem>
                {availableJobTitles.slice(1).map((title) => (
                  <MenuItem key={title} value={title}>
                    {title}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth size="small" sx={{ mb: 1.5 }}>
              <InputLabel>Filter by Day Availability</InputLabel>
              <Select
                value={selectedDayFilter}
                label="Filter by Day Availability"
                onChange={(e) => setSelectedDayFilter(e.target.value)}
                startAdornment={<CalendarToday fontSize="small" sx={{ mr: 1 }} />}
              >
                <MenuItem value="all">
                  <em>Any Day</em>
                </MenuItem>
                {weekInfo.days.map(day => (
                  <MenuItem key={day.date} value={day.date}>
                    {day.dayName} ({new Date(day.date).getDate()}/{new Date(day.date).getMonth() + 1}){day.isWeekend ? ' — Weekend' : ''}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth size="small">
              <InputLabel>Filter Available Shifts by Ward</InputLabel>
              <Select
                value={selectedWardFilter}
                label="Filter Available Shifts by Ward"
                onChange={(e) => setSelectedWardFilter(e.target.value)}
                startAdornment={<FilterList fontSize="small" sx={{ mr: 1 }} />}
              >
                <MenuItem value="all">
                  <em>All Wards</em>
                </MenuItem>
                {availableWards.slice(1).map((ward) => (
                  <MenuItem key={ward} value={ward}>
                    {ward}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            
            <Box sx={{ mt: 2, p: 1, bgcolor: 'white', borderRadius: 1 }}>
              <Typography variant="body2" color="textSecondary">
                <strong>Active Filters:</strong> {getFilterSummary()}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                <strong>Showing:</strong> {filteredStaffList.length} staff
              </Typography>
            </Box>
          </Paper>
        </Grid>
      </Grid>
      
      <Grid container spacing={3}>
        {/* Main Schedule Table */}
        <Grid item xs={9}>
          
      




<Paper elevation={3} sx={{ overflow: 'hidden', width: '100%' }} className="print-page">
  <Box sx={{ 
    width: '100%',
    overflow: 'hidden'
  }}>
    {/* Fixed Header Row */}
    <Grid container sx={fixedHeaderStyle}>
      {/* Staff column - fixed width */}
      <Grid item sx={{ width: { xs: '160px', sm: '200px', md: '220px' } }}>
        <Box sx={{ 
          p: { xs: 1, sm: 2 },
          borderRight: 1, 
          borderBottom: 1, 
          borderColor: 'divider',
          bgcolor: 'primary.main',
          color: 'white',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center'
        }}>
          <Box>
            <Typography fontWeight="bold" sx={{ fontSize: { xs: '0.9rem', sm: '1rem' } }}>
              Staff
            </Typography>
            {selectedJobTitleFilter !== 'all' && (
              <Typography variant="caption" sx={{ display: 'block', opacity: 0.8, fontSize: { xs: '0.6rem', sm: '0.7rem' } }}>
                Filtered: {selectedJobTitleFilter}
              </Typography>
            )}
          </Box>
          <Typography variant="caption" sx={{ opacity: 0.8, fontSize: { xs: '0.6rem', sm: '0.7rem' } }}>
            Actions
          </Typography>
        </Box>
      </Grid>
      
      {/* Day columns - each with equal width using flex: 1 */}
      {weekInfo.days.map(day => (
        <Grid item key={day.date} sx={{ flex: 1, minWidth: { xs: '100px', sm: '120px', md: '140px' } }}>
          <Box sx={{ 
            p: { xs: 1, sm: 2 },
            borderBottom: 1, 
            borderColor: 'divider',
            bgcolor: day.isWeekend ? 'secondary.main' : 'primary.light',
            color: 'white',
            textAlign: 'center',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center'
          }}>
            <Typography fontWeight="bold" sx={{ fontSize: { xs: '0.8rem', sm: '0.9rem' } }}>
              {day.shortName}
            </Typography>
            <Typography variant="body2" sx={{ fontSize: { xs: '0.7rem', sm: '0.8rem' } }}>
              {new Date(day.date).getDate()}/{new Date(day.date).getMonth() + 1}
            </Typography>
            {day.isWeekend && (
              <Typography variant="caption" sx={{ fontStyle: 'italic', fontSize: { xs: '0.6rem', sm: '0.7rem' } }}>
                {day.dayName === 'Sunday' ? 'Sunday' : 'Weekend'}
              </Typography>
            )}
          </Box>
        </Grid>
      ))}
    </Grid>
    
    {/* Scrollable Container for Staff Rows */}
    <Box sx={scrollableTableStyle}>
      {filteredStaffList.map((staff, index) => {
        const staffShiftsData = staffShifts[staff._id] || {};
        const staffAvailabilitiesData = staffAvailabilities[staff._id] || {};
        
        return (
          <Grid container key={staff._id} className="staff-row print-page" sx={{ width: '100%' }}>
            {/* Staff Column - fixed width matching header */}
            <Grid item sx={{ width: { xs: '160px', sm: '200px', md: '220px' } }}>
              <Box sx={{ 
                p: { xs: 1, sm: 2 },
                borderRight: 1, 
                borderBottom: 1, 
                borderColor: 'divider',
                bgcolor: index % 2 === 0 ? 'grey.50' : 'white',
                minHeight: { xs: '120px', sm: '140px' },
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                height: '100%'
              }}>
                <Box>
                  <Typography fontWeight="bold" sx={{ fontSize: { xs: '0.9rem', sm: '1rem' } }}>
                    {staff.name}
                  </Typography>
                  <Typography variant="body2" color="textSecondary" sx={{ fontSize: { xs: '0.7rem', sm: '0.8rem' } }}>
                    {staff.jobtitle}
                  </Typography>
                  <Typography variant="caption" color="textSecondary" sx={{ fontSize: { xs: '0.6rem', sm: '0.7rem' }, display: 'block' }}>
                    {staff.speciality || 'General'}
                  </Typography>
                  <Typography variant="caption" color="textSecondary" sx={{ fontSize: { xs: '0.55rem', sm: '0.65rem' }, display: 'block' }}>
                    {staff.email}
                  </Typography>
                </Box>
                
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<Email />}
                  onClick={() => handleEmailButtonClick(staff._id)}
                  sx={{ 
                    mt: 1,
                    bgcolor: '#1976d2',
                    '&:hover': {
                      bgcolor: '#1565c0'
                    },
                    fontSize: { xs: '0.6rem', sm: '0.7rem' },
                    minWidth: { xs: 'auto', sm: '100px' }
                  }}
                  className="no-print"
                >
                  Email
                </Button>
              </Box>
            </Grid>
            
            {/* Day Columns - each with flex: 1 matching header */}
            {weekInfo.days.map(day => {
              const shifts = staffShiftsData[day.date] || [];
              const availabilities = staffAvailabilitiesData[day.date] || [];

              // Separate unavailability from regular availability
              const unavailCards = availabilities.filter(a => a.isUnavailable || a.isSickLeave);
              const regularAvailCards = availabilities.filter(a => {
                if (a.isUnavailable || a.isSickLeave) return false;
                // Hide regular availability only if it's fully covered by an assigned shift
                return !isAvailabilityCoveredByShift(a, shifts);
              });

              const nothingVisible = shifts.length === 0 && unavailCards.length === 0 && regularAvailCards.length === 0;

              return (
              <Grid item key={`${staff._id}-${day.date}`} sx={{ flex: 1, minWidth: { xs: '100px', sm: '120px', md: '140px' } }}>
                <Box
                  sx={{
                    p: { xs: 0.5, sm: 1 },
                    minHeight: { xs: '120px', sm: '140px' },
                    borderRight: 1,
                    borderBottom: 1,
                    borderColor: 'divider',
                    bgcolor: day.isWeekend ? 'grey.100' : (index % 2 === 0 ? 'grey.50' : 'white'),
                    cursor: 'pointer',
                    overflow: 'hidden',
                    height: '100%'
                  }}
                  onDragOver={handleDragOver}
                  onDrop={(e) => handleDrop(e, staff._id, day.date)}
                  className="no-print"
                >
                  {/* ── Unavailability cards — always visible ── */}
                  {unavailCards.map((availability, idx) =>
                    renderAvailabilityCard(availability, idx, staff._id, day.date)
                  )}

                  {/* ── Assigned shift cards ── */}
                  {shifts.map((shift, idx) => (
                    <Tooltip 
                      key={`${shift.id}-${idx}`}
                      title={`${shift.categoryOfStaff} - ${shift.ward} - Click to edit, double-click to release`}
                      arrow
                      className="no-print"
                    >
                      <Card 
                        sx={{ 
                          mb: 0.5, 
                          bgcolor: getShiftColor(shift),
                          color: 'white',
                          '&:hover': {
                            boxShadow: 3,
                            transform: 'translateY(-2px)',
                            transition: 'all 0.2s',
                            cursor: 'pointer'
                          }
                        }}
                        onDoubleClick={() => handleShiftDoubleClick(shift, staff._id, day.date)}
                        onClick={(e) => {
                          if (e.ctrlKey || e.metaKey) {
                            handleEditShiftClick(shift, staff._id, day.date);
                          }
                        }}
                        className="shift-card"
                      >
                        <CardContent sx={{ p: { xs: 0.5, sm: 1 }, position: 'relative' }}>
                          <IconButton
                            size="small"
                            sx={{
                              position: 'absolute',
                              top: { xs: 0, sm: 2 },
                              right: { xs: 0, sm: 2 },
                              color: 'white',
                              backgroundColor: 'rgba(0,0,0,0.2)',
                              padding: { xs: '1px', sm: '2px' },
                              '&:hover': {
                                backgroundColor: 'rgba(0,0,0,0.4)',
                              }
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEditShiftClick(shift, staff._id, day.date);
                            }}
                            className="no-print"
                          >
                            <Edit fontSize="small" sx={{ fontSize: { xs: '0.7rem', sm: '0.9rem' } }} />
                          </IconButton>
                          
                          <Typography variant="body2" sx={{ fontSize: { xs: '0.6rem', sm: '0.8rem' }, pr: { xs: 2, sm: 3 } }}>
                            {shift.startTime} - {shift.endTime}
                          </Typography>
                          <Typography variant="caption" sx={{ fontSize: { xs: '0.55rem', sm: '0.7rem' }, display: 'block' }}>
                            {shift.ward.length > 15 ? shift.ward.substring(0, 12) + '...' : shift.ward}
                          </Typography>
                          <Typography variant="caption" sx={{ fontSize: { xs: '0.5rem', sm: '0.65rem' }, display: 'block' }}>
                            {shift.categoryOfStaff.length > 10 ? shift.categoryOfStaff.substring(0, 8) + '...' : shift.categoryOfStaff}
                          </Typography>
                          {shift.shiftData?.isOvertime && (
                            <Chip label="OT" size="small" sx={{ height: 14, fontSize: '0.55rem', fontWeight: 'bold', bgcolor: '#e65100', color: 'white', mt: 0.5, '& .MuiChip-label': { px: 0.5 } }} />
                          )}

                          {isShiftApproved(shift) && (
                              <Chip
                                label="Approved"
                                size="small"
                                sx={{ height: 14, fontSize: '0.55rem', fontWeight: 'bold', bgcolor: '#2e7d32', color: 'white', mt: 0.5, ml: 0.5, '& .MuiChip-label': { px: 0.5 } }}
                              />
                            )}
                          {!isShiftApproved(shift) && isShiftPendingApproval(shift) && (
                              <Chip
                                label="Not Approved Yet"
                                size="small"
                                sx={{ height: 14, fontSize: '0.55rem', fontWeight: 'bold', bgcolor: '#b71c1c', color: 'white', mt: 0.5, ml: 0.5, '& .MuiChip-label': { px: 0.5 } }}
                              />
                            )}
                        </CardContent>
                      </Card>
                    </Tooltip>
                  ))}
                  
                  {/* ── Regular availability cards — hidden if covered by shift ── */}
                  {regularAvailCards.map((availability, idx) =>
                    renderAvailabilityCard(availability, idx, staff._id, day.date)
                  )}
                  
                  {/* ── Empty state ── */}
                  {nothingVisible && (
                    <Box 
                      sx={{ 
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#999',
                        fontStyle: 'italic',
                        cursor: 'context-menu',
                        fontSize: { xs: '0.6rem', sm: '0.7rem' },
                        '&:hover': {
                          bgcolor: 'action.hover',
                          border: '1px dashed',
                          borderColor: 'primary.main',
                        }
                      }}
                      onClick={(e) => {
                        if (e.detail === 2) {
                          handleOpenCreateAvailability(staff, day);
                        }
                      }}
                      onContextMenu={(e) => {
                        e.preventDefault();
                        handleOpenCreateAvailability(staff, day);
                      }}
                      className="no-print"
                    >
                      <Typography variant="caption" sx={{ fontSize: { xs: '0.6rem', sm: '0.7rem' } }}>
                        No shifts/availability
                      </Typography>
                      <Typography variant="caption" sx={{ fontSize: { xs: '0.5rem', sm: '0.6rem' } }}>
                        Double-click: Create
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Grid>
              );
            })}
          </Grid>
        );
      })}
      
      {/* No results message */}
      {filteredStaffList.length === 0 && (
        <Grid container sx={{ width: '100%' }}>
          <Grid item xs={12}>
            <Box sx={{ 
              p: 4, 
              textAlign: 'center',
              borderBottom: 1,
              borderColor: 'divider'
            }}>
              <Typography variant="body1" color="textSecondary">
                {isManager && managerDirectorate 
                  ? `No staff members match the current filters in ${managerDirectorate}`
                  : 'No staff members match the current filters.'
                }
              </Typography>
              <Button 
                onClick={handleClearFilters}
                sx={{ mt: 1 }}
                className="no-print"
              >
                Clear Filters
              </Button>
            </Box>
          </Grid>
        </Grid>
      )}
    </Box>
  </Box>
</Paper>
        </Grid>
        
        {/* Available Shifts Panel */}
        <Grid item xs={3} className="no-print">
          <Paper elevation={3} sx={{ p: 2, height: '100%' }}>
            <Box sx={{ mb: 2 }}>
              <Typography variant="h6" gutterBottom>
                Available Shifts
                {isManager && managerDirectorate && (
                  <Typography variant="caption" color="primary" sx={{ display: 'block' }}>
                    ({managerDirectorate}
                    {inclusionIds.size > 0 ? ` +${inclusionIds.size} included` : ''})
                  </Typography>
                )}
              </Typography>
              
              {selectedWardFilter !== 'all' && (
                <Chip 
                  label={`Ward: ${selectedWardFilter}`}
                  size="small"
                  color="primary"
                  onDelete={() => setSelectedWardFilter('all')}
                  sx={{ mb: 1 }}
                />
              )}

              <FormControl fullWidth size="small" sx={{ mb: 1.5 }}>
                <InputLabel>Filter by Staff Member</InputLabel>
                <Select
                  value={selectedStaffFilter}
                  label="Filter by Staff Member"
                  onChange={(e) => setSelectedStaffFilter(e.target.value)}
                >
                  <MenuItem value="all"><em>All Staff (no exclusions applied)</em></MenuItem>
                  {[...staffList].sort((a, b) => (a.name || '').localeCompare(b.name || '')).map(staff => (
                    <MenuItem key={staff._id} value={staff._id}>
                      {staff.name}
                      {staff.wardExclusions?.length > 0 && (
                        <Chip
                          label={`${staff.wardExclusions.length} excl.`}
                          size="small"
                          sx={{ ml: 1, height: 16, fontSize: '0.6rem', bgcolor: '#d32f2f', color: 'white' }}
                        />
                      )}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {selectedStaffFilter !== 'all' && (() => {
                const staffForExclusions = staffList.find(s => s._id === selectedStaffFilter);
                return staffForExclusions?.wardExclusions?.length > 0 ? (
                  <Box sx={{ mb: 1, p: 1, bgcolor: '#ffebee', borderRadius: 1, border: '1px solid #ef9a9a' }}>
                    <Typography variant="caption" sx={{ color: '#c62828', fontWeight: 'bold', display: 'block' }}>
                      ⛔ Excluded wards hidden:
                    </Typography>
                    {staffForExclusions.wardExclusions.map(ward => (
                      <Chip
                        key={ward}
                        label={ward}
                        size="small"
                        sx={{ m: 0.25, height: 16, fontSize: '0.6rem', bgcolor: '#d32f2f', color: 'white' }}
                      />
                    ))}
                  </Box>
                ) : null;
              })()}
              
              <Typography variant="body2" color="textSecondary" paragraph>
                Drag to assign to staff • sorted by date/time
              </Typography>
            </Box>
            
            <Box sx={{ maxHeight: '600px', overflow: 'auto' }}>
              {filteredAvailableShifts.length === 0 ? (
                <Alert severity={availableShifts.length === 0 ? "info" : "warning"}>
                  {availableShifts.length === 0 
                    ? `No available shifts for this week ${isManager && managerDirectorate ? `in ${managerDirectorate}` : ''}`
                    : "No available shifts match the selected ward filter"}
                  {selectedWardFilter !== 'all' && (
                    <Button 
                      size="small" 
                      onClick={() => setSelectedWardFilter('all')}
                      sx={{ ml: 1 }}
                    >
                      Clear Filter
                    </Button>
                  )}
                </Alert>
              ) : (
                filteredAvailableShifts.map((shift, index) => {
                  const shiftDate = new Date(shift.startDateTime);
                  const dayOfWeek = shiftDate.getDay();
                  const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
                  
                  return (
                    <Card
                      key={shift._id}
                      draggable
                      onDragStart={(e) => {
                        e.dataTransfer.setData('text/plain', JSON.stringify(shift));
                        setDraggedItem(shift);
                      }}
                      sx={{ 
                        mb: 2, 
                        cursor: 'grab',
                        bgcolor: isWeekend ? SHIFT_STATUS_COLORS.weekendAvailable : SHIFT_STATUS_COLORS.available,
                        color: 'white',
                        '&:hover': { 
                          transform: 'translateY(-2px)',
                          transition: 'transform 0.2s',
                          boxShadow: 3
                        }
                      }}
                    >
                      <CardContent>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Typography variant="subtitle2">
                            {shiftDate.toLocaleDateString()}
                          </Typography>
                          {isWeekend && (
                            <Chip 
                              label="Weekend" 
                              size="small" 
                              sx={{ 
                                backgroundColor: 'rgba(255,255,255,0.3)',
                                color: 'white',
                                fontSize: '0.7rem'
                              }} 
                            />
                          )}
                        </Box>
                        <Typography variant="body2" fontWeight="bold">
                          {shiftDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} - 
                          {new Date(shift.endDateTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </Typography>
                        <Typography variant="body2">
                          {shift.wardDepartmentArea}
                        </Typography>
                        <Typography variant="caption" sx={{ opacity: 0.9, display: 'block' }}>
                          {shift.categoryOfStaff || 'Staff'}
                        </Typography>
                        <Typography variant="caption" sx={{ opacity: 0.9 }}>
                          {shift.hospitalLocationId?.name}
                        </Typography>
                      </CardContent>
                    </Card>
                  );
                })
              )}
            </Box>
          </Paper>
        </Grid>
      </Grid>
      
      {/* NEW: Add/Edit Availability Dialog (from StaffAvailability) */}
      <Dialog open={availabilityDialogOpen} onClose={handleCloseAvailabilityDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editMode ? 'Edit Availability' : 'Add Availability'}
          <Typography variant="subtitle2" color="textSecondary">
            {selectedDayForAvailability && formatDate(selectedDayForAvailability.date)}
          </Typography>
          {selectedStaffForAvailability && (
            <Typography variant="body2" color="primary" sx={{ mt: 0.5 }}>
              For: {selectedStaffForAvailability.name} ({selectedStaffForAvailability.jobtitle})
            </Typography>
          )}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            {/* Shift Presets */}
            <Box sx={{ mb: 3, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
              <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <WatchLater fontSize="small" />
                Quick Shift Presets
              </Typography>
              <Grid container spacing={1}>
                {Object.entries(SHIFT_PRESETS).map(([key, preset]) => (
                  <Grid item xs={6} sm={4} key={key}>
                    <Button
                      fullWidth
                      variant={selectedPreset === key ? "contained" : "outlined"}
                      size="small"
                      onClick={() => handlePresetChange(key)}
                      sx={{ 
                        justifyContent: 'flex-start',
                        textAlign: 'left',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis'
                      }}
                    >
                      {preset.label}
                    </Button>
                  </Grid>
                ))}
              </Grid>
              {selectedPreset && (
                <Typography variant="caption" color="primary" sx={{ mt: 1, display: 'block' }}>
                  Selected: {SHIFT_PRESETS[selectedPreset].label}
                </Typography>
              )}
            </Box>
            
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Start Time"
                  type="datetime-local"
                  name="startDateTime"
                  value={formData.startDateTime}
                  onChange={handleStartDateChange}
                  InputLabelProps={{ shrink: true }}
                  helperText="End time auto-sets to 8 hours later"
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="End Time"
                  type="datetime-local"
                  name="endDateTime"
                  value={formData.endDateTime}
                  onChange={handleEndDateChange}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Category of Staff"
                  name="categoryOfStaff"
                  value={formData.categoryOfStaff}
                  onChange={handleFormChange}
                  helperText={
                    selectedStaffForAvailability 
                      ? `Based on selected staff's job title: ${selectedStaffForAvailability.jobtitle}`
                      : ''
                  }
                />
              </Grid>
              
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Ward/Department</InputLabel>
                  <Select
                    name="wardDepartmentArea"
                    value={formData.wardDepartmentArea}
                    onChange={handleFormChange}
                    label="Ward/Department"
                  >
                    <MenuItem value="">Select Ward...</MenuItem>
                    {filteredHospitalLocations.map(location => (
                      <MenuItem key={location._id} value={location.wardDepartmentArea}>
                        {location.wardDepartmentArea}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Comments (Optional)"
                  name="comments"
                  value={formData.comments}
                  onChange={handleFormChange}
                  multiline
                  rows={3}
                  placeholder="Any additional notes about the availability..."
                />
              </Grid>

              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.isUnavailable}
                      onChange={(e) => {
                        const isUnavailable = e.target.checked;
                        setFormData(prev => ({
                          ...prev,
                          isUnavailable: isUnavailable,
                          unavailabilityReason: isUnavailable ? prev.unavailabilityReason : '',
                          unavailabilityType: isUnavailable ? 'Full Day' : 'Full Day',
                          unavailabilityDetails: isUnavailable ? prev.unavailabilityDetails : '',
                          status: isUnavailable ? 'Approved' : 'Open'
                        }));
                      }}
                      color="error"
                    />
                  }
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{ 
                        width: 16, 
                        height: 16, 
                        bgcolor: 'error.light',
                        borderRadius: '2px',
                        border: '1px solid #d32f2f'
                      }} />
                      <Typography variant="body2" color={formData.isUnavailable ? 'error.main' : 'text.primary'}>
                        Mark as Unavailable
                      </Typography>
                      {formData.isUnavailable && (
                        <LocalHospital fontSize="small" color="error" />
                      )}
                    </Box>
                  }
                />
              </Grid>

              {formData.isUnavailable && (
                <>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Unavailability Reason</InputLabel>
                      <Select
                        value={formData.unavailabilityReason}
                        label="Unavailability Reason"
                        onChange={(e) => setFormData(prev => ({
                          ...prev,
                          unavailabilityReason: e.target.value
                        }))}
                      >
                        <MenuItem value="">Select Reason...</MenuItem>
                        {UNAVAILABILITY_REASONS.map((reason) => (
                          <MenuItem key={reason} value={reason}>
                            {reason}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Unavailability Type</InputLabel>
                      <Select
                        value={formData.unavailabilityType}
                        label="Unavailability Type"
                        onChange={(e) => setFormData(prev => ({
                          ...prev,
                          unavailabilityType: e.target.value
                        }))}
                      >
                        <MenuItem value="Full Day">Full Day</MenuItem>
                        <MenuItem value="Half Day AM">Half Day (Morning)</MenuItem>
                        <MenuItem value="Half Day PM">Half Day (Afternoon)</MenuItem>
                        <MenuItem value="Partial Day">Partial Day</MenuItem>
                        <MenuItem value="Multi-Day">Multi-Day</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Unavailability Details (Optional)"
                      value={formData.unavailabilityDetails}
                      onChange={(e) => setFormData(prev => ({
                        ...prev,
                        unavailabilityDetails: e.target.value
                      }))}
                      multiline
                      rows={2}
                      placeholder="Additional details about the unavailability..."
                      size="small"
                    />
                  </Grid>
                </>
              )}

              {formData.isSickLeave && (
                <>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Sick Leave Type</InputLabel>
                      <Select
                        value={formData.sickLeaveType}
                        label="Sick Leave Type"
                        onChange={(e) => setFormData(prev => ({
                          ...prev,
                          sickLeaveType: e.target.value
                        }))}
                      >
                        <MenuItem value="Full Day">Full Day</MenuItem>
                        <MenuItem value="Half Day AM">Half Day (Morning)</MenuItem>
                        <MenuItem value="Half Day PM">Half Day (Afternoon)</MenuItem>
                        <MenuItem value="Partial Day">Partial Day</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Sickness Reason (Optional)"
                      value={formData.sicknessReason}
                      onChange={(e) => setFormData(prev => ({
                        ...prev,
                        sicknessReason: e.target.value
                      }))}
                      placeholder="e.g., Flu, Injury, etc."
                      size="small"
                    />
                  </Grid>
                </>
              )}
              
              {/* Show existing availabilities for this day */}
              {selectedStaffForAvailability && selectedDayForAvailability && 
               staffAvailabilities[selectedStaffForAvailability._id]?.[selectedDayForAvailability.date]?.length > 0 && (
                <Grid item xs={12}>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="subtitle2" gutterBottom>
                    Existing availabilities for this day:
                  </Typography>
                  <List dense>
                    {staffAvailabilities[selectedStaffForAvailability._id][selectedDayForAvailability.date]
                      .filter(a => !editMode || a.id !== editingAvailability?.id)
                      .map((availability) => (
                        <ListItem 
                          key={availability.id}
                          sx={{ 
                            bgcolor: 'grey.50',
                            borderRadius: 1,
                            mb: 0.5
                          }}
                        >
                          <ListItemText
                            primary={`${availability.startTime} - ${availability.endTime}`}
                            secondary={
                              <span>
                                {formatHoursMinutes(availability.durationMinutes)} • {availability.status}
                                {availability.createdByManager && (
                                  <span style={{ marginLeft: '8px', fontStyle: 'italic', color: '#666' }}>
                                    (Added by manager)
                                  </span>
                                )}
                              </span>
                            }
                          />
                          <ListItemSecondaryAction>
                            <MuiIconButton
                              size="small"
                              onClick={() => handleOpenEditAvailability(availability, selectedStaffForAvailability)}
                            >
                              <Edit fontSize="small" />
                            </MuiIconButton>
                            <MuiIconButton
                              size="small"
                              onClick={(e) => handleDeleteClick(availability.id, e)}
                              sx={{ ml: 0.5 }}
                            >
                              <Delete fontSize="small" />
                            </MuiIconButton>
                          </ListItemSecondaryAction>
                        </ListItem>
                      ))}
                  </List>
                </Grid>
              )}
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAvailabilityDialog}>
            Cancel
          </Button>

          {editMode && editingAvailability && (
            <Button 
              onClick={handleDeleteConfirmOpen}
              variant="outlined" 
              color="error"
              startIcon={<Delete />}
              sx={{ mr: 'auto' }}
            >
              Remove Availability
            </Button>
          )}

          <Button 
            onClick={handleFormSubmit} 
            variant="contained" 
            startIcon={editMode ? <Edit /> : <Add />}
          >
            {editMode ? 'Update' : 'Add'} Availability
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onClose={handleDeleteConfirmClose} maxWidth="sm" fullWidth>
        <DialogTitle>Remove Availability</DialogTitle>
        <DialogContent>
          <Typography variant="body1" paragraph>
            Are you sure you want to remove this availability?
          </Typography>
          {editingAvailability && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2">
                <strong>Time:</strong> {formatTime(editingAvailability.startDateTime)} - {formatTime(editingAvailability.endDateTime)}
              </Typography>
              <Typography variant="body2">
                <strong>Date:</strong> {formatDate(editingAvailability.startDateTime)}
              </Typography>
              {(editingAvailability.isUnavailable || editingAvailability.isSickLeave) && (
                <Typography variant="body2">
                  <strong>Type:</strong> Unavailable ({editingAvailability.unavailabilityReason || editingAvailability.sicknessReason || 'No reason specified'})
                </Typography>
              )}
            </Box>
          )}
          <Typography variant="body2" color="textSecondary" sx={{ mt: 2, fontStyle: 'italic' }}>
            Note: This action can be reversed by an administrator.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteConfirmClose}>Cancel</Button>
          <Button 
            onClick={handleConfirmDelete} 
            variant="contained" 
            color="error"
            startIcon={<Delete />}
          >
            Remove Availability
          </Button>
        </DialogActions>
      </Dialog>

      {/* Release Shift Confirmation Dialog */}
      <Dialog open={releaseDialogOpen} onClose={handleCancelRelease} maxWidth="sm" fullWidth className="no-print">
        <DialogTitle>Release Shift</DialogTitle>
        <DialogContent>
          <Typography variant="body1" paragraph>
            Are you sure you want to release this shift?
          </Typography>
          {shiftToRelease && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2">
                <strong>Staff:</strong> {shiftToRelease.staffName}
              </Typography>
              <Typography variant="body2">
                <strong>Date:</strong> {shiftToRelease.shiftDate}
              </Typography>
              <Typography variant="body2">
                <strong>Time:</strong> {shiftToRelease.shiftTime}
              </Typography>
              <Typography variant="body2">
                <strong>Ward:</strong> {shiftToRelease.ward}
              </Typography>
            </Box>
          )}
          <Typography variant="body2" color="textSecondary" sx={{ mt: 2, fontStyle: 'italic' }}>
            This will make the shift available for assignment to another staff member.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelRelease}>Cancel</Button>
          <Button 
            onClick={handleReleaseShift} 
            variant="contained" 
            color="warning"
            startIcon={<Delete />}
          >
            Release Shift
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Edit Shift Dialog */}
      <Dialog open={editDialogOpen} onClose={handleCancelEdit} maxWidth="lg" fullWidth className="no-print">
        <DialogTitle>Edit Shift Details</DialogTitle>
        <DialogContent>
          {shiftToEdit && (
            <Box sx={{ mt: 1 }}>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                Editing shift for: <strong>{shiftToEdit.staffName}</strong>
              </Typography>
              
              {/* Tabs for Basic Info and Schedule */}
              <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                <Tabs 
                  value={activeTab} 
                  onChange={(e, newValue) => setActiveTab(newValue)}
                  sx={{ mb: 2 }}
                >
                  <Tab label="Basic Information" value="basic" />
                  <Tab label="Schedule" value="schedule" />
                </Tabs>
              </Box>
              
              {/* Tab Content */}
              {activeTab === 'basic' && (
                <Grid container spacing={2} sx={{ mt: 1 }}>
                  {/* Top Row: Hospital Location, Category, Reason */}
                  <Grid item xs={4}>
                    <FormControl fullWidth margin="normal">
                      <InputLabel>Hospital Location</InputLabel>
                      <Select
                        name="hospitalLocationId"
                        value={editForm.hospitalLocationId || ''}
                        label="Hospital Location"
                        onChange={handleEditFormChange}
                        size="small"
                      >
                        {filteredHospitalLocations?.map(location => (
                          <MenuItem key={location._id} value={location._id}>
                            {location.wardDepartmentArea} 
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  
                  <Grid item xs={4}>
                    <FormControl fullWidth margin="normal">
                      <InputLabel>Category of Staff</InputLabel>
                      <Select
                        name="categoryOfStaff"
                        value={editForm.categoryOfStaff || ''}
                        label="Category of Staff"
                        onChange={handleEditFormChange}
                        size="small"
                      >
                        <MenuItem value="">Select Category...</MenuItem>
                        {CATEGORY_OF_STAFF.map(c => (
                          <MenuItem key={c.value} value={c.value}>
                            {c.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  
                  <Grid item xs={4}>
                    <FormControl fullWidth margin="normal">
                      <InputLabel>Reason for Request</InputLabel>
                      <Select
                        name="reasonForRequest"
                        value={editForm.reasonForRequest || ''}
                        label="Reason for Request"
                        onChange={handleEditFormChange}
                        size="small"
                      >
                        <MenuItem value="">Select Reason...</MenuItem>
                        {REASON_FOR_REQUEST.map(r => (
                          <MenuItem key={r.value} value={r.value}>
                            {r.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  {/* ← add this block */}
                    {['1-1 special', 'Cohorts'].some(
                      r => editForm.reasonForRequest?.toLowerCase() === r.toLowerCase()
                    ) && (
                      <Grid item xs={4}>
                        <FormControl fullWidth margin="normal" required>
                          <InputLabel>Specials / Cohorts Reason</InputLabel>
                          <Select
                            name="specialsReason"
                            value={editForm.specialsReason || ''}
                            label="Specials / Cohorts Reason"
                            onChange={handleEditFormChange}
                            size="small"
                          >
                            <MenuItem value="">Select reason...</MenuItem>
                            {SPECIALS_REASON.map(r => (
                              <MenuItem key={r.value} value={r.value}>{r.label}</MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                    )}
                  
                  {/* Middle Row: Reason Details */}
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Reason for Request Details"
                      name="reasonForRequestDetails"
                      value={editForm.reasonForRequestDetails || ''}
                      onChange={handleEditFormChange}
                      margin="normal"
                      multiline
                      rows={3}
                      placeholder="Provide details about why this shift is needed..."
                    />
                  </Grid>
                  
                  {/* Options Row */}
                  <Grid item xs={12}>
                    <Paper variant="outlined" sx={{ p: 2, bgcolor: '#f8f9fa' }}>
                      <Typography variant="subtitle2" gutterBottom>
                        Options
                      </Typography>
                      <Grid container spacing={2}>
                        <Grid item xs={4}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={editForm.escort || false}
                                onChange={(e) => setEditForm(prev => ({ ...prev, escort: e.target.checked }))}
                                name="escort"
                              />
                            }
                            label="Escort Required"
                          />
                        </Grid>
                        <Grid item xs={4}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={editForm.special || false}
                                onChange={(e) => setEditForm(prev => ({ ...prev, special: e.target.checked }))}
                                name="special"
                              />
                            }
                            label="Is this a Special"
                          />
                        </Grid>
                        <Grid item xs={4}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={editForm.isOvertime || false}
                                onChange={(e) => setEditForm(prev => ({ ...prev, isOvertime: e.target.checked }))}
                                name="isOvertime"
                              />
                            }
                            label="Overtime Shift"
                          />
                        </Grid>
                        <Grid item xs={4}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={editForm.isActive !== false}
                                onChange={(e) => setEditForm(prev => ({ ...prev, isActive: e.target.checked }))}
                                name="isActive"
                              />
                            }
                            label="Active Shift"
                          />
                          <Typography variant="caption" color="textSecondary" sx={{ display: 'block', ml: 4 }}>
                            Uncheck to "delete" the shift (sets isActive=false)
                          </Typography>
                        </Grid>
                        <Grid item xs={8}>
                          <TextField
                            fullWidth
                            label="ECC Approval Number"
                            name="eccNumber"
                            value={editForm.eccNumber || ''}
                            onChange={handleEditFormChange}
                            size="small"
                            placeholder="Enter ECC number..."
                          />
                        </Grid>
                      </Grid>
                    </Paper>
                  </Grid>
                  
                  {/* Special Requirements and Comments */}
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      label="Special Requirements"
                      name="specialRequirements"
                      value={editForm.specialRequirements || ''}
                      onChange={handleEditFormChange}
                      margin="normal"
                      multiline
                      rows={4}
                      placeholder="Any special equipment, training, or requirements..."
                    />
                  </Grid>
                  
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      label="Comments"
                      name="comments"
                      value={editForm.comments || ''}
                      onChange={handleEditFormChange}
                      margin="normal"
                      multiline
                      rows={4}
                      placeholder="Additional notes or comments..."
                    />
                  </Grid>
                </Grid>
              )}
              
              {activeTab === 'schedule' && (
                <Grid container spacing={2} sx={{ mt: 1 }}>
                  {/* Shift Time Presets */}
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" gutterBottom>
                      Shift Time Preset
                    </Typography>
                    <Paper variant="outlined" sx={{ p: 2 }}>
                      <Grid container spacing={1}>
                        {Object.entries(SHIFT_PRESETS).map(([key, preset]) => (
                          <Grid item xs={6} md={4} key={key}>
                            <Paper
                              elevation={editForm.selectedPreset === key ? 2 : 0}
                              sx={{
                                p: 1,
                                cursor: 'pointer',
                                bgcolor: editForm.selectedPreset === key ? 'primary.main' : 'grey.100',
                                color: editForm.selectedPreset === key ? 'white' : 'inherit',
                                '&:hover': {
                                  bgcolor: editForm.selectedPreset === key ? 'primary.dark' : 'grey.200',
                                }
                              }}
                              onClick={() => handlePresetSelect(key)}
                            >
                              <Typography variant="body2">
                                {preset.label}
                              </Typography>
                            </Paper>
                          </Grid>
                        ))}
                      </Grid>
                    </Paper>
                  </Grid>
                  
                  {/* Date and Time Fields */}
                  <Grid item xs={3}>
                    <TextField
                      fullWidth
                      label="Start Date"
                      type="date"
                      name="startDate"
                      value={editForm.startDate || ''}
                      onChange={handleEditFormChange}
                      InputLabelProps={{ shrink: true }}
                      margin="normal"
                      size="small"
                    />
                  </Grid>
                  
                  <Grid item xs={3}>
                    <TextField
                      fullWidth
                      label="End Date (Same Day)"
                      type="date"
                      name="endDate"
                      value={editForm.endDate || ''}
                      onChange={handleEditFormChange}
                      InputLabelProps={{ shrink: true }}
                      margin="normal"
                      size="small"
                      helperText="Same day unless overnight shift"
                    />
                  </Grid>
                  
                  <Grid item xs={3}>
                    <TextField
                      fullWidth
                      label="Start Time"
                      type="time"
                      name="startTime"
                      value={editForm.startTime || ''}
                      onChange={handleEditFormChange}
                      InputLabelProps={{ shrink: true }}
                      margin="normal"
                      size="small"
                    />
                  </Grid>
                  
                  <Grid item xs={3}>
                    <TextField
                      fullWidth
                      label="End Time"
                      type="time"
                      name="endTime"
                      value={editForm.endTime || ''}
                      onChange={handleEditFormChange}
                      InputLabelProps={{ shrink: true }}
                      margin="normal"
                      size="small"
                    />
                  </Grid>
                  
                  {/* Shift Duration Display */}
                  <Grid item xs={12}>
                    <Alert severity="info" sx={{ mt: 1 }}>
                      <Typography variant="body2">
                        <strong>Shift Duration:</strong> {calculateShiftDuration()}
                      </Typography>
                    </Alert>
                  </Grid>
                </Grid>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelEdit}>Cancel</Button>
          <Button 
              onClick={handleUpdateShift} 
              variant="contained" 
              color="primary"
              startIcon={<Edit />}
              disabled={
                ['1-1 special', 'Cohorts'].some(r => editForm.reasonForRequest?.toLowerCase() === r.toLowerCase()) 
                  && !editForm.specialsReason
              }
            >
              Update Shift
            </Button>
        </DialogActions>
      </Dialog>
      
      {/* Email Dialog (for web email fallback) */}
      <Dialog open={emailDialogOpen} onClose={handleCancelEmail} maxWidth="md" fullWidth className="no-print">
        <DialogTitle>Email Weekly Schedule</DialogTitle>
        <DialogContent>
          {selectedStaffForEmail && (
            <Box sx={{ mt: 1 }}>
              <Box sx={{ mb: 3, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                <Typography variant="body1" gutterBottom>
                  <strong>Recipient:</strong> {selectedStaffForEmail.name}
                </Typography>
                <Typography variant="body2">
                  <strong>Email:</strong> {selectedStaffForEmail.email}
                </Typography>
                <Typography variant="body2">
                  <strong>Week:</strong> {formatDateRange()}
                </Typography>
                
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Email Method:
                  </Typography>
                  <ToggleButtonGroup
                    value={emailMethod}
                    exclusive
                    onChange={(e, newMethod) => newMethod && setEmailMethod(newMethod)}
                    size="small"
                  >
                    <ToggleButton value="outlook">
                      <DesktopWindows sx={{ mr: 1 }} />
                      Outlook
                    </ToggleButton>
                    <ToggleButton value="web">
                      <Language sx={{ mr: 1 }} />
                      Web Email
                    </ToggleButton>
                  </ToggleButtonGroup>
                  
                  <FormControlLabel
                    control={
                      <Switch
                        checked={includeWeekend}
                        onChange={(e) => setIncludeWeekend(e.target.checked)}
                        size="small"
                      />
                    }
                    label="Include Weekend Days"
                    sx={{ mt: 1, display: 'block' }}
                  />
                </Box>
              </Box>
              
              {emailMethod === 'web' && (
                <>
                  <TextField
                    fullWidth
                    label="Email Subject"
                    value={emailSubject}
                    onChange={(e) => setEmailSubject(e.target.value)}
                    margin="normal"
                  />
                  
                  <TextField
                    fullWidth
                    label="Additional Notes (optional)"
                    value={emailContent}
                    onChange={(e) => setEmailContent(e.target.value)}
                    margin="normal"
                    multiline
                    rows={4}
                    helperText="Add any additional notes or instructions for the staff member"
                  />
                  
                  <Divider sx={{ my: 2 }} />
                  
                  <Typography variant="subtitle2" gutterBottom>
                    Schedule Preview:
                  </Typography>
                  <Paper elevation={0} sx={{ p: 2, bgcolor: '#f9f9f9', maxHeight: '200px', overflow: 'auto' }}>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace', fontSize: '0.8rem' }}>
                      {generateSchedulePlainText(selectedStaffForEmail.id).slice(0, 500)}...
                    </Typography>
                  </Paper>
                </>
              )}
              
              {emailMethod === 'outlook' && (
                <Alert severity="info" sx={{ mt: 2 }}>
                  <Typography variant="body2">
                    Clicking "Send Email" will open Microsoft Outlook with the schedule pre-populated.
                    If Outlook is not installed, the system will automatically fall back to web email.
                  </Typography>
                </Alert>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelEmail}>Cancel</Button>
          <Button 
            onClick={handleSendEmail} 
            variant="contained" 
            color="primary"
            startIcon={emailMethod === 'outlook' ? <DesktopWindows /> : <Send />}
          >
            {emailMethod === 'outlook' ? 'Open in Outlook' : 'Send Web Email'}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        message={snackbar.message}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        className="no-print"
      />
    </Box>
  );
};

export default StaffWeeklyView;